import {
  ChangeDetectorRef,
  Component,
  Injector,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ViewChildren,
  QueryList,
  AfterViewInit,
  Inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { FbEmrMedicalHistoryWidget } from '../../../models/form-widget-emr-medical-history.model';
import { WidgetBase } from '../../../models/widget-base';
import { TokenToDivPipe } from '../../../helpers/token-to-div.pipe';
import { MatDividerModule } from '@angular/material/divider';
import { FbInputListComponent } from './components/fb-input-list';
import { CheckboxItem, FbEmrCheckboxListComponent } from './components/fb-emr-checkbox-list';
import { FbEmrTableComponent } from './components/fb-emr-table';
import { WidgetApiService } from '../../../rx-form-presenter/services/widget-api.service';
import { DateValidationService } from '../../../services/date-validation.service';
import { parseLocalDateString, isValidDateForDiscrepancy } from '../../../helpers/date-utils';
import { normalizeVisibility } from '../../../helpers/visibility.util';
import { AddChronicConditionDialogComponent } from './components/dialogs/add-chronic-condition-dialog.component';
import { AddAllergyDialogComponent } from './components/dialogs/add-allergy-dialog.component';
import { AddMedicationDialogComponent } from './components/dialogs/add-medication-dialog.component';
import { AddSurgeryDialogComponent } from './components/dialogs/add-surgery-dialog.component';
import { RX_APP_CONTEXT, RxAppContext } from '@nrwl/rx-root-state';
import {
  preserveInputListScribeMarkers,
  preserveMedicationScribeMarkers,
} from './medical-history-scribe-marker.util';
import { clearScribeSuggestionMarker } from '../../scribe-suggestion-marker.util';

@Component({
  selector: 'fb-emr-medical-history',
  templateUrl: './fb-emr-medical-history.component.html',
  styleUrls: ['./fb-emr-medical-history.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TokenToDivPipe,
    MatDividerModule,
    MatDialogModule,
    FbInputListComponent,
    FbEmrCheckboxListComponent,
    FbEmrTableComponent,
  ],
  standalone: true,
})
export class FbEmrMedicalHistoryComponent extends WidgetBase implements OnInit, AfterViewInit {
  @Input() public isTemplatePrint = false;
  @Output() onDiscrepanciesDetected = new EventEmitter<any[]>();
  @ViewChildren(FbInputListComponent) inputLists!: QueryList<FbInputListComponent>;
  @ViewChildren(FbEmrTableComponent) tables!: QueryList<FbEmrTableComponent>;
  private _savedFormData: any;
  private conflictingFields: Map<
    string,
    {
      saved: any;
      current: any;
      savedDate?: string;
      currentDate?: string;
      savedStopDate?: string;
      currentStopDate?: string;
      savedFreq?: string;
      currentFreq?: string;
      savedDosage?: string;
      currentDosage?: string;
    }
  > = new Map();
  @Input()
  public override set payload(value) {
    this._payload = value;
  }
  public override get payload() {
    return this._payload;
  }

  private readonly defaultCategoryRows = new WeakMap<object, any[]>();
  private readonly defaultCheckboxItems = new WeakMap<object, CheckboxItem[]>();

  /**
   * True while a patient's own plan is being customized. The widget then draws its defaults and
   * never reads or writes the answers stored in the category, so they are saved back unchanged.
   */
  public get isInstanceDesign(): boolean {
    return !!this.isDesign && !!this.entity;
  }

  public getCategoryValues(category: any): any[] {
    if (!this.isInstanceDesign) {
      return category.value;
    }

    let rows = this.defaultCategoryRows.get(category);
    if (!rows) {
      rows = [];
      this.defaultCategoryRows.set(category, rows);
    }
    return rows;
  }

  public setCategoryValues(category: any, values: any[]): void {
    if (this.isInstanceDesign) {
      return;
    }
    category.value = values;
  }

  public getCheckboxItems(category: any): CheckboxItem[] {
    if (!this.isInstanceDesign) {
      return category.items;
    }

    let items = this.defaultCheckboxItems.get(category);
    if (!items) {
      items = (category.items ?? []).map((item: CheckboxItem) => ({ ...item, value: false }));
      this.defaultCheckboxItems.set(category, items);
    }
    return items;
  }

  private _lastLoadedPatientId: number | null = null;
  private _isLoadingMedicalHistory = false;
  private _hasLoadedCatalogOptions = false;
  private chronicConditionOptionsSnapshot: Array<{ label: string; value: string | number }> = [];
  private allergyOptionsSnapshot: Array<{ label: string; value: string | number }> = [];
  private medicationOptionsSnapshot: Array<{ label: string; value: string | number }> = [];
  private surgeryOptionsSnapshot: Array<{ label: string; value: string | number }> = [];

  @Input()
  public override set patientId(value) {
    this._patientId = value;
    if (this.isDesign) {
      return;
    }
    const patientRemoteId = this.patient?.remote_id;
    if (patientRemoteId && !this.isPreview && !this.isPrinting) {
      if (this._lastLoadedPatientId !== patientRemoteId && !this._isLoadingMedicalHistory) {
        this._lastLoadedPatientId = patientRemoteId;
        this.loadMedicalHistory(patientRemoteId);
      }
    }
  }
  public override get patientId() {
    return this._patientId;
  }

  @Input()
  public override set patient(value) {
    this._patient = value;
    if (this.isDesign) {
      return;
    }
    if (value?.remote_id && !this.patientId && !this.isPreview && !this.isPrinting) {
      if (this._lastLoadedPatientId !== value.remote_id && !this._isLoadingMedicalHistory) {
        this._lastLoadedPatientId = value.remote_id;
        this.loadMedicalHistory(value.remote_id);
      }
    }
  }
  public override get patient() {
    return this._patient;
  }

  private _loadPending = false;
  private _hasLoadedMedicalHistory = false;

  @Input()
  public override set isPreview(value: boolean) {
    this._isPreview = value;
    if (!value && !this.isPrinting && this._loadPending) {
      this.checkAndLoadMedicalHistory();
    }
  }
  public override get isPreview() {
    return this._isPreview;
  }

  @Input()
  public override set isPrinting(value: boolean) {
    this._isPrinting = value;
    if (!value && !this.isPreview && this._loadPending) {
      this.checkAndLoadMedicalHistory();
    }
  }
  public override get isPrinting() {
    return this._isPrinting;
  }

  @Input()
  public set savedFormData(value: any) {
    this._savedFormData = value;
  }

  public get savedFormData(): any {
    return this._savedFormData;
  }

  private checkAndLoadMedicalHistory() {
    if (this.isDesign) {
      return;
    }
    const patientRemoteId = this.patient?.remote_id;
    if (patientRemoteId && !this.isPreview && !this.isPrinting) {
      this._loadPending = false;
      this.loadMedicalHistory(patientRemoteId);
    } else if (patientRemoteId) {
      this._loadPending = true;
    }
  }

  constructor(
    private injector: Injector,
    private widgetApiService: WidgetApiService,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef,
    private dateValidationService: DateValidationService,
    @Inject(RX_APP_CONTEXT)
    private appContext: RxAppContext,
  ) {
    super(injector);
  }

  private get isRxStaffContext(): boolean {
    return this.appContext === RxAppContext.RxStaff;
  }

  ngOnInit() {
    // WARNING: Payload is not available here, see libs/rx-form-builder/src/lib/form-element/form-element.component.ts
  }

  ngAfterViewInit() {
    if (this.isDesign) {
      setTimeout(() => {
        this.ready.next(true);
      });
      return;
    }

    const patientRemoteId = this.patient?.remote_id;
    if (patientRemoteId && !this.isPreview && !this.isPrinting && !this._hasLoadedMedicalHistory) {
      this.loadMedicalHistory(patientRemoteId);
    } else if (!patientRemoteId || this.isPreview || this.isPrinting) {
      setTimeout(() => {
        this.ready.next(true);
      });
    }

    this.ensureCatalogOptionsLoaded();
  }

  public getMedicalHistoryConditionOptionsSnapshot(): Array<{
    label: string;
    value: string | number;
  }> {
    return this.chronicConditionOptionsSnapshot;
  }

  public getMedicalHistoryAllergyOptionsSnapshot(): Array<{
    label: string;
    value: string | number;
  }> {
    return this.allergyOptionsSnapshot;
  }

  public getMedicalHistoryMedicationOptionsSnapshot(): Array<{
    label: string;
    value: string | number;
  }> {
    return this.medicationOptionsSnapshot;
  }

  public getMedicalHistorySurgeryOptionsSnapshot(): Array<{
    label: string;
    value: string | number;
  }> {
    return this.surgeryOptionsSnapshot;
  }

  private ensureCatalogOptionsLoaded(): void {
    if (this._hasLoadedCatalogOptions || this.isDesign || this.isRxStaffContext) {
      return;
    }

    forkJoin({
      chronicConditions: this.widgetApiService.getChronicConditions(),
      allergies: this.widgetApiService.getAllergies(),
      medications: this.widgetApiService.getMedications(),
      surgeries: this.widgetApiService.getSurgeries(),
    }).subscribe({
      next: ({ chronicConditions, allergies, medications, surgeries }) => {
        this.chronicConditionOptionsSnapshot = this.normalizeCatalogOptions(
          chronicConditions,
          'ChronicId',
          'ChronicName',
        );
        this.allergyOptionsSnapshot = this.normalizeCatalogOptions(
          allergies,
          'AllergyId',
          'AllergyName',
        );
        this.medicationOptionsSnapshot = this.normalizeCatalogOptions(
          medications,
          'MedicationId',
          'MedicationName',
        );
        this.surgeryOptionsSnapshot = this.normalizeCatalogOptions(
          surgeries,
          'SurgeryId',
          'SurgeryName',
        );
        this._hasLoadedCatalogOptions = true;
      },
      error: () => {
        this.chronicConditionOptionsSnapshot = [];
        this.allergyOptionsSnapshot = [];
        this.medicationOptionsSnapshot = [];
        this.surgeryOptionsSnapshot = [];
        this._hasLoadedCatalogOptions = false;
      },
    });
  }

  private normalizeCatalogOptions(
    items: unknown[],
    valueKey: string,
    labelKey: string,
  ): Array<{ label: string; value: string | number }> {
    if (!Array.isArray(items)) {
      return [];
    }

    return items
      .map((item: any) => ({
        value: item?.[valueKey] ?? null,
        label: item?.[labelKey] ?? null,
      }))
      .filter(
        (option): option is { label: string; value: string | number } =>
          option.value !== null &&
          option.value !== undefined &&
          `${option.value}`.trim() !== '' &&
          option.label !== null &&
          option.label !== undefined &&
          `${option.label}`.trim() !== '',
      )
      .map((option) => ({
        value: typeof option.value === 'number' ? option.value : `${option.value}`.trim(),
        label: `${option.label}`.trim(),
      }));
  }

  private loadMedicalHistory(patientRemoteId: number) {
    if (this.isRxStaffContext) {
      this.ready.next(true);
      return;
    }

    if (!patientRemoteId) {
      this.ready.next(true);
      return;
    }

    if (this._hasLoadedMedicalHistory) {
      this.ready.next(true);
      return;
    }

    this._hasLoadedMedicalHistory = true;
    this._isLoadingMedicalHistory = true;

    forkJoin({
      surgeries: this.widgetApiService.getCustomerSurgery({ CustomerId: patientRemoteId }),
      chronicConditions: this.widgetApiService.getCustomerChronicCondition({
        CustomerId: patientRemoteId,
      }),
      medications: this.widgetApiService.getCustomerMedication({ CustomerId: patientRemoteId }),
      allergies: this.widgetApiService.getCustomerAllergy({ CustomerId: patientRemoteId }),
    }).subscribe({
      next: (data) => {
        this.prefillMedicalHistory(data);

        this._isLoadingMedicalHistory = false;
        setTimeout(() => {
          this.ready.next(true);
        });
      },
      error: (error) => {
        this._hasLoadedMedicalHistory = false;
        this._isLoadingMedicalHistory = false;
        this.ready.next(true);
      },
    });
  }

  private applyFieldConflicts(
    row: any[],
    category: string,
    itemId: string | number,
    fieldMappings: { dateField: string; valueField: string },
  ): void {
    if (!itemId || !row || row.length < 2) return;

    const dateConflictKey = `${category}-${fieldMappings.dateField}-${itemId}`;
    if (this.conflictingFields.has(dateConflictKey)) {
      const conflict = this.conflictingFields.get(dateConflictKey);
      row[0].hasConflict = true;
      row[0].conflictTooltip = `PatientNow: ${conflict?.current || conflict?.currentDate || 'none'}`;
    }

    const valueConflictKey = `${category}-${fieldMappings.valueField}-${itemId}`;
    if (this.conflictingFields.has(valueConflictKey)) {
      const conflict = this.conflictingFields.get(valueConflictKey);
      row[1].hasConflict = true;
      row[1].conflictTooltip = `PatientNow: ${conflict?.current || 'none'}`;
    }
  }

  private applyMedicationConflicts(medication: any, medicationId: string | number): void {
    if (!medicationId || !medication) return;

    if (!medication.hasConflict) {
      medication.hasConflict = {};
    }
    if (!medication.conflictTooltip) {
      medication.conflictTooltip = {};
    }

    const conflictChecks = [
      { field: 'Date', category: 'Medications - Start Date' },
      { field: 'Medication', category: 'Medications - Name' },
      { field: 'Freq', category: 'Medications - Frequency' },
      { field: 'Dosage', category: 'Medications - Dosage' },
      { field: 'Stop', category: 'Medications - Stop Date' },
    ];

    conflictChecks.forEach((check) => {
      const conflictKey = `${check.category}-${medicationId}`;
      if (this.conflictingFields.has(conflictKey)) {
        const conflict = this.conflictingFields.get(conflictKey);

        medication.hasConflict[check.field] = true;
        medication.conflictTooltip[check.field] = `PatientNow: ${conflict?.current || 'none'}`;
      }
    });
  }

  private prefillMedicalHistory(data: any) {
    if (!this.payload?.properties?.items?.value) {
      return;
    }

    const items = this.payload.properties.items.value;
    items.forEach((item) => {
      this.normalizeCategoryVisibility(item);
    });

    const formStatus = this.entity?.status;
    const isFormIncomplete = formStatus !== undefined && formStatus < 5;
    const hasSavedMetadata = this.entity?.metadata?.medical_history;

    if (isFormIncomplete && hasSavedMetadata) {
      this.prefillFromMetadata(this.entity.metadata.medical_history);
    }

    let discrepancies: any[] = [];

    if (isFormIncomplete && hasSavedMetadata) {
      console.log('✌️ DISCREPANCY DETECTION - Metadata vs PatientNow');
      discrepancies = this.detectDiscrepanciesFromMetadata(
        this.entity.metadata.medical_history,
        data,
      );
      console.log('✌️ Discrepancies detected:', discrepancies.length, discrepancies);
      if (discrepancies.length > 0 && !this.isPreview) {
        this.onDiscrepanciesDetected.emit(discrepancies);
      }
    } else if (this._savedFormData) {
      discrepancies = this.detectDiscrepancies(this._savedFormData, data);

      this.conflictingFields.clear();
      discrepancies.forEach((d) => {
        if (d.category === 'medications' && d.field && d.field.startsWith('Medication - ')) {
          const savedValue = d.savedValue || '';
          const currentValue = d.currentValue || '';
          const medicationId = d.savedId || d.currentId;

          const mainParts = savedValue.split(' | ');
          const currentMainParts = currentValue.split(' | ');

          const datePattern = /^\d{4}-\d{2}-\d{2}/;
          const firstPartIsDate =
            mainParts[0] && (datePattern.test(mainParts[0]) || mainParts[0].includes(' to '));

          if (firstPartIsDate) {
            const datePart = mainParts[0];
            const currentDatePart = currentMainParts[0] || '';

            if (datePart.includes(' to ')) {
              const [startDate, stopDate] = datePart.split(' to ');
              const [currentStartDate, currentStopDate] = currentDatePart.split(' to ');

              if (startDate !== currentStartDate) {
                this.conflictingFields.set(`Medications - Start Date-${medicationId}`, {
                  saved: startDate,
                  current: currentStartDate || '',
                });
              }
              if (stopDate !== currentStopDate) {
                this.conflictingFields.set(`Medications - Stop Date-${medicationId}`, {
                  saved: stopDate,
                  current: currentStopDate || '',
                });
              }
            } else {
              this.conflictingFields.set(`Medications - Start Date-${medicationId}`, {
                saved: datePart,
                current: currentDatePart,
              });
            }

            if (mainParts.length > 1) {
              const freq = mainParts[1] || '';
              const dosage = mainParts[2] || '';
              const currentFreq = currentMainParts[1] || '';
              const currentDosage = currentMainParts[2] || '';

              if (freq !== currentFreq) {
                this.conflictingFields.set(`Medications - Frequency-${medicationId}`, {
                  saved: freq,
                  current: currentFreq,
                });
              }
              if (dosage !== currentDosage) {
                this.conflictingFields.set(`Medications - Dosage-${medicationId}`, {
                  saved: dosage,
                  current: currentDosage,
                });
              }
            }
          } else if (mainParts.length > 0) {
            if (mainParts.length === 2) {
              this.conflictingFields.set(`Medications - Frequency-${medicationId}`, {
                saved: mainParts[0] || '',
                current: currentMainParts[0] || '',
              });
              this.conflictingFields.set(`Medications - Dosage-${medicationId}`, {
                saved: mainParts[1] || '',
                current: currentMainParts[1] || '',
              });
            } else if (mainParts.length === 1 && !datePattern.test(mainParts[0])) {
              if (!d.savedValue.includes(' | ')) {
                this.conflictingFields.set(`Medications - Name-${medicationId}`, {
                  saved: savedValue,
                  current: currentValue,
                });
              } else {
                this.conflictingFields.set(`Medications - Frequency-${medicationId}`, {
                  saved: mainParts[0] || '',
                  current: currentMainParts[0] || '',
                });
              }
            }
          }
        } else if (
          (d.category === 'other_chronic_conditions' ||
            d.category === 'allergies' ||
            d.category === 'surgeries') &&
          d.field
        ) {
          const itemId = d.savedId || d.currentId;
          const savedValue = d.savedValue || '';
          const currentValue = d.currentValue || '';

          const datePattern = /^\d{4}-\d{2}-\d{2}$/;
          if (datePattern.test(savedValue) && datePattern.test(currentValue)) {
            const fieldType =
              d.category === 'other_chronic_conditions'
                ? 'Chronic Condition Date'
                : d.category === 'allergies'
                  ? 'Allergy Date'
                  : d.category === 'surgeries'
                    ? 'Surgery Date'
                    : '';
            this.conflictingFields.set(`${d.category}-${fieldType}-${itemId}`, {
              saved: savedValue,
              current: currentValue,
            });
          } else if (savedValue.includes(' | ')) {
            const nameFieldType =
              d.category === 'other_chronic_conditions'
                ? 'Chronic Condition'
                : d.category === 'allergies'
                  ? 'Allergy'
                  : d.category === 'surgeries'
                    ? 'Surgery'
                    : '';
            const itemKey = `${d.category}-${nameFieldType}-${itemId}`;
            this.conflictingFields.set(itemKey, {
              saved: savedValue.split(' | ')[0],
              current: currentValue.split(' | ')[0],
            });

            const fieldType =
              d.category === 'other_chronic_conditions'
                ? 'Chronic Condition Date'
                : d.category === 'allergies'
                  ? 'Allergy Date'
                  : d.category === 'surgeries'
                    ? 'Surgery Date'
                    : '';
            this.conflictingFields.set(`${d.category}-${fieldType}-${itemId}`, {
              saved: savedValue.split(' | ')[1],
              current: currentValue.split(' | ')[1],
            });
          } else {
            const fieldType =
              d.category === 'other_chronic_conditions'
                ? 'Chronic Condition'
                : d.category === 'allergies'
                  ? 'Allergy'
                  : d.category === 'surgeries'
                    ? 'Surgery'
                    : '';
            const itemKey = `${d.category}-${fieldType}-${itemId}`;
            this.conflictingFields.set(itemKey, {
              saved: savedValue,
              current: currentValue,
            });
          }
        } else if (d.category && d.category.startsWith('Medications - ')) {
          const fieldKey = `${d.category}-${d.savedId || d.currentId}`;
          this.conflictingFields.set(fieldKey, {
            saved: d.savedValue,
            current: d.currentValue,
          });
        }
      });

      if (discrepancies.length > 0 && !this.isPreview) {
        this.onDiscrepanciesDetected.emit(discrepancies);
      }
    }

    const surgeriesItem = items.find((item) => item.name === 'surgeries');

    if (surgeriesItem && data.surgeries?.length > 0) {
      const hasNonEmptySurgeries = surgeriesItem.value?.some((row: any) => {
        if (Array.isArray(row) && row.length >= 2) {
          const surgeryField = row[1];
          return surgeryField?.value && surgeryField.value.trim() !== '';
        }
        return false;
      });

      if (!surgeriesItem.value || surgeriesItem.value.length === 0 || !hasNonEmptySurgeries) {
        surgeriesItem.value = data.surgeries.map((surgery) => {
          const surgeryId = surgery.Id;
          const conflictKey = `surgeries-${surgeryId}`;
          const hasConflict = this.conflictingFields.has(conflictKey);

          return [
            {
              title: 'Diagnose Date',
              value:
                surgery.SurgeryDate &&
                !this.dateValidationService.isInvalidNetDate(surgery.SurgeryDate)
                  ? surgery.SurgeryDate.split('T')[0]
                  : '',
              type: 'date',
              required: true,
            },
            {
              title: 'Surgery',
              value: hasConflict
                ? this.conflictingFields.get(conflictKey).saved
                : surgery.SurgeryName || '',
              placeholder: 'Enter surgeries',
              required: true,
              id: surgeryId,
              hasConflict: hasConflict,
              conflictTooltip: hasConflict
                ? this.buildConflictTooltip(this.conflictingFields.get(conflictKey))
                : undefined,
            },
          ];
        });
      } else {
        const existingIds = new Set();
        const existingNames = new Set();

        surgeriesItem.value.forEach((row: any) => {
          if (Array.isArray(row) && row.length >= 2) {
            const surgeryField = row[1];
            const surgeryId = surgeryField?.id;

            if (surgeryId) {
              this.applyFieldConflicts(row, 'surgeries', surgeryId, {
                dateField: 'Surgery Date',
                valueField: 'Surgery Name',
              });
              existingIds.add(String(surgeryId));
            }
            if (surgeryField?.value && surgeryField.value.trim()) {
              existingNames.add(surgeryField.value.toLowerCase().trim());
            }
          }
        });

        const newSurgeries = data.surgeries.filter((surgery) => {
          const surgeryId = String(surgery.Id || '');
          const surgeryName = (surgery.SurgeryName || '').trim();
          const surgeryDate =
            surgery.SurgeryDate && !this.dateValidationService.isInvalidNetDate(surgery.SurgeryDate)
              ? surgery.SurgeryDate.split('T')[0]
              : '';

          if (surgeryId && existingIds.has(surgeryId)) {
            return false;
          }

          const conflictKey = `surgeries-Surgery-${surgeryId}`;
          if (this.conflictingFields.has(conflictKey)) {
            const conflict = this.conflictingFields.get(conflictKey);
            if (conflict.saved === 'none') {
              return false;
            }
          }

          const hasExactMatch = surgeriesItem.value.some((row: any) => {
            if (Array.isArray(row) && row.length >= 2) {
              const dateField = row[0];
              const nameField = row[1];
              const existingDate = (dateField?.value || '').trim();
              const existingName = (nameField?.value || '').trim();

              return existingName === surgeryName && existingDate === surgeryDate;
            }
            return false;
          });

          if (hasExactMatch) {
            return false;
          }

          return true;
        });

        if (newSurgeries.length > 0) {
          const newSurgeryRows = newSurgeries.map((surgery) => {
            const surgeryId = surgery.Id;
            const conflictKey = `surgeries-${surgeryId}`;
            const hasConflict = this.conflictingFields.has(conflictKey);

            return [
              {
                title: 'Diagnose Date',
                value:
                  surgery.SurgeryDate &&
                  !this.dateValidationService.isInvalidNetDate(surgery.SurgeryDate)
                    ? surgery.SurgeryDate.split('T')[0]
                    : '',
                type: 'date',
                required: true,
              },
              {
                title: 'Surgery',
                value: hasConflict
                  ? this.conflictingFields.get(conflictKey).saved
                  : surgery.SurgeryName || '',
                placeholder: 'Enter surgeries',
                required: true,
                id: surgeryId,
                hasConflict: hasConflict,
                conflictTooltip: hasConflict
                  ? this.buildConflictTooltip(this.conflictingFields.get(conflictKey))
                  : undefined,
              },
            ];
          });

          surgeriesItem.value = [...surgeriesItem.value, ...newSurgeryRows];
        }
      }
    }

    const chronicConditionsItem = items.find((item) => item.name === 'chronic_conditions');
    const checkedConditionNames = new Set<string>();

    if (
      chronicConditionsItem &&
      chronicConditionsItem.items &&
      data.chronicConditions?.length > 0
    ) {
      data.chronicConditions.forEach((condition) => {
        const conditionName = (condition.ChronicName || '').trim();
        if (conditionName) {
          const checkboxItem = chronicConditionsItem.items.find(
            (item) => item.title && item.title.toLowerCase() === conditionName.toLowerCase(),
          );
          if (checkboxItem) {
            checkboxItem.value = true;
            checkboxItem.id = condition.Id;
            checkedConditionNames.add(conditionName.toLowerCase());
          }
        }
      });
    }

    const otherChronicItem = items.find((item) => item.name === 'other_chronic_conditions');

    if (otherChronicItem && data.chronicConditions?.length > 0) {
      const conditionsForOtherSection = data.chronicConditions.filter((condition) => {
        const conditionName = (condition.ChronicName || '').trim();
        return conditionName && !checkedConditionNames.has(conditionName.toLowerCase());
      });

      const existingOtherRows = Array.isArray(otherChronicItem.value) ? otherChronicItem.value : [];
      const hasNonEmptyConditions = existingOtherRows.some((row: any) => {
        if (Array.isArray(row) && row.length >= 2) {
          const conditionField = row[1];
          return conditionField?.value && conditionField.value.trim() !== '';
        }
        return false;
      });

      if (existingOtherRows.length === 0 || !hasNonEmptyConditions) {
        otherChronicItem.value = conditionsForOtherSection.map((condition) => {
          const conditionId = condition.Id;
          const conflictKey = `conditions-${conditionId}`;
          const hasConflict = this.conflictingFields.has(conflictKey);

          return [
            {
              title: 'Diagnose Date',
              value:
                condition.OnsetDate &&
                !this.dateValidationService.isInvalidNetDate(condition.OnsetDate)
                  ? condition.OnsetDate.split('T')[0]
                  : '',
              type: 'date',
              required: true,
            },
            {
              title: 'Condition',
              value: hasConflict
                ? this.conflictingFields.get(conflictKey).saved
                : condition.ChronicName || '',
              placeholder: 'Enter other conditions',
              required: true,
              id: conditionId,
              hasConflict: hasConflict,
              conflictTooltip: hasConflict
                ? this.buildConflictTooltip(this.conflictingFields.get(conflictKey))
                : undefined,
            },
          ];
        });
      } else {
        const existingIds = new Set();
        const existingNames = new Set();

        existingOtherRows.forEach((row: any) => {
          if (Array.isArray(row) && row.length >= 2) {
            const conditionField = row[1];
            const conditionId = conditionField?.id;

            if (conditionId) {
              this.applyFieldConflicts(row, 'other_chronic_conditions', conditionId, {
                dateField: 'Chronic Condition Date',
                valueField: 'Chronic Condition Name',
              });
              existingIds.add(String(conditionId));
            }
            if (conditionField?.value && conditionField.value.trim()) {
              existingNames.add(conditionField.value.toLowerCase().trim());
            }
          }
        });

        const newConditions = conditionsForOtherSection.filter((condition) => {
          const conditionId = String(condition.Id || '');
          const conditionName = (condition.ChronicName || '').trim();
          const conditionDate =
            condition.OnsetDate && !this.dateValidationService.isInvalidNetDate(condition.OnsetDate)
              ? condition.OnsetDate.split('T')[0]
              : '';

          if (conditionId && existingIds.has(conditionId)) {
            return false;
          }

          const conflictKey = `other_chronic_conditions-Chronic Condition-${conditionId}`;
          if (this.conflictingFields.has(conflictKey)) {
            const conflict = this.conflictingFields.get(conflictKey);
            if (conflict.saved === 'none') {
              return false;
            }
          }

          const hasExactMatch = existingOtherRows.some((row: any) => {
            if (Array.isArray(row) && row.length >= 2) {
              const dateField = row[0];
              const nameField = row[1];
              const existingDate = (dateField?.value || '').trim();
              const existingName = (nameField?.value || '').trim();

              return existingName === conditionName && existingDate === conditionDate;
            }
            return false;
          });

          if (hasExactMatch) {
            return false;
          }

          return true;
        });

        if (newConditions.length > 0) {
          const newConditionRows = newConditions.map((condition) => {
            const conditionId = condition.Id;
            const conflictKey = `conditions-${conditionId}`;
            const hasConflict = this.conflictingFields.has(conflictKey);

            return [
              {
                title: 'Diagnose Date',
                value:
                  condition.OnsetDate &&
                  !this.dateValidationService.isInvalidNetDate(condition.OnsetDate)
                    ? condition.OnsetDate.split('T')[0]
                    : '',
                type: 'date',
                required: true,
              },
              {
                title: 'Condition',
                value: hasConflict
                  ? this.conflictingFields.get(conflictKey).saved
                  : condition.ChronicName || '',
                placeholder: 'Enter other conditions',
                required: true,
                id: conditionId,
                hasConflict: hasConflict,
                conflictTooltip: hasConflict
                  ? this.buildConflictTooltip(this.conflictingFields.get(conflictKey))
                  : undefined,
              },
            ];
          });

          otherChronicItem.value = [...existingOtherRows, ...newConditionRows];
        }
      }
    }

    const allergiesItem = items.find((item) => item.name === 'allergies');

    if (allergiesItem && data.allergies?.length > 0) {
      const existingAllergies = Array.isArray(allergiesItem.value) ? allergiesItem.value : [];
      const hasNonEmptyAllergies = existingAllergies.some((row: any) => {
        if (Array.isArray(row) && row.length >= 2) {
          const allergyField = row[1];
          return allergyField?.value && allergyField.value.trim() !== '';
        }
        return false;
      });

      if (existingAllergies.length === 0 || !hasNonEmptyAllergies) {
        allergiesItem.value = data.allergies.map((allergy) => {
          const allergyId = allergy.Id;
          const conflictKey = `allergies-${allergyId}`;
          const hasConflict = this.conflictingFields.has(conflictKey);

          return [
            {
              title: 'Diagnose Date',
              value:
                allergy.DiagDate && !this.dateValidationService.isInvalidNetDate(allergy.DiagDate)
                  ? allergy.DiagDate.split('T')[0]
                  : '',
              type: 'date',
              required: true,
            },
            {
              title: 'Allergy',
              value: hasConflict
                ? this.conflictingFields.get(conflictKey).saved
                : allergy.AllergyName || '',
              placeholder: 'Enter allergies',
              required: true,
              id: allergyId,
              hasConflict: hasConflict,
              conflictTooltip: hasConflict
                ? this.buildConflictTooltip(this.conflictingFields.get(conflictKey))
                : undefined,
            },
          ];
        });
      } else {
        const existingIds = new Set();
        const existingNames = new Set();

        existingAllergies.forEach((row: any) => {
          if (Array.isArray(row) && row.length >= 2) {
            const allergyField = row[1];
            const allergyId = allergyField?.id;

            if (allergyId) {
              this.applyFieldConflicts(row, 'allergies', allergyId, {
                dateField: 'Allergy Date',
                valueField: 'Allergy Name',
              });
              existingIds.add(String(allergyId));
            }
            if (allergyField?.value && allergyField.value.trim()) {
              existingNames.add(allergyField.value.toLowerCase().trim());
            }
          }
        });

        const newAllergies = data.allergies.filter((allergy) => {
          const allergyId = String(allergy.Id || '');
          const allergyName = (allergy.AllergyName || '').trim();
          const allergyDate =
            allergy.DiagDate && !this.dateValidationService.isInvalidNetDate(allergy.DiagDate)
              ? allergy.DiagDate.split('T')[0]
              : '';

          if (allergyId && existingIds.has(allergyId)) {
            return false;
          }

          const conflictKey = `allergies-Allergy-${allergyId}`;
          if (this.conflictingFields.has(conflictKey)) {
            const conflict = this.conflictingFields.get(conflictKey);
            if (conflict.saved === 'none') {
              return false;
            }
          }

          const hasExactMatch = existingAllergies.some((row: any) => {
            if (Array.isArray(row) && row.length >= 2) {
              const dateField = row[0];
              const nameField = row[1];
              const existingDate = (dateField?.value || '').trim();
              const existingName = (nameField?.value || '').trim();

              return existingName === allergyName && existingDate === allergyDate;
            }
            return false;
          });

          if (hasExactMatch) {
            return false;
          }

          return true;
        });

        if (newAllergies.length > 0) {
          const newAllergyRows = newAllergies.map((allergy) => {
            const allergyId = allergy.Id;
            const conflictKey = `allergies-${allergyId}`;
            const hasConflict = this.conflictingFields.has(conflictKey);

            return [
              {
                title: 'Diagnose Date',
                value:
                  allergy.DiagDate && !this.dateValidationService.isInvalidNetDate(allergy.DiagDate)
                    ? allergy.DiagDate.split('T')[0]
                    : '',
                type: 'date',
                required: true,
              },
              {
                title: 'Allergy',
                value: hasConflict
                  ? this.conflictingFields.get(conflictKey).saved
                  : allergy.AllergyName || '',
                placeholder: 'Enter allergies',
                required: true,
                id: allergyId,
                hasConflict: hasConflict,
                conflictTooltip: hasConflict
                  ? this.buildConflictTooltip(this.conflictingFields.get(conflictKey))
                  : undefined,
              },
            ];
          });

          allergiesItem.value = [...existingAllergies, ...newAllergyRows];
        }
      }
    }

    const medicationsItem = items.find((item) => item.name === 'medications');

    if (medicationsItem && data.medications?.length > 0) {
      if (
        !medicationsItem.value ||
        medicationsItem.value.length === 0 ||
        (medicationsItem.value.length === 1 && this.isTableRowEmpty(medicationsItem.value[0]))
      ) {
        medicationsItem.value = data.medications.map((medication) => {
          const medicationId = medication.Id;
          const conflictKey = `medications-${medicationId}`;
          const hasConflict = this.conflictingFields.has(conflictKey);

          return {
            Date:
              medication.StartDate &&
              !this.dateValidationService.isInvalidNetDate(medication.StartDate)
                ? medication.StartDate.split('T')[0]
                : '',
            Medication: hasConflict
              ? this.conflictingFields.get(conflictKey).saved
              : medication.MedicationName || '',
            Freq: medication.Freq || '',
            Dosage: medication.Dosage || '',
            Stop:
              medication.StopDate &&
              !this.dateValidationService.isInvalidNetDate(medication.StopDate)
                ? medication.StopDate.split('T')[0]
                : '',
            id: medicationId,
            hasConflict: hasConflict,
            conflictTooltip: hasConflict
              ? this.buildConflictTooltip(this.conflictingFields.get(conflictKey))
              : undefined,
          };
        });
      } else {
        const hasNonEmptyMedications = medicationsItem.value.some(
          (med) => med.Medication && med.Medication.trim() !== '',
        );

        if (!hasNonEmptyMedications) {
          medicationsItem.value = data.medications.map((medication) => {
            const medicationId = medication.Id;
            const conflictKey = `medications-${medicationId}`;
            const hasConflict = this.conflictingFields.has(conflictKey);

            return {
              Date:
                medication.StartDate &&
                !this.dateValidationService.isInvalidNetDate(medication.StartDate)
                  ? medication.StartDate.split('T')[0]
                  : '',
              Medication: hasConflict
                ? this.conflictingFields.get(conflictKey).saved
                : medication.MedicationName || '',
              Freq: medication.Freq || '',
              Dosage: medication.Dosage || '',
              Stop:
                medication.StopDate &&
                !this.dateValidationService.isInvalidNetDate(medication.StopDate)
                  ? medication.StopDate.split('T')[0]
                  : '',
              id: medicationId,
              hasConflict: hasConflict,
              conflictTooltip: hasConflict
                ? this.buildConflictTooltip(this.conflictingFields.get(conflictKey))
                : undefined,
            };
          });
        } else {
          medicationsItem.value.forEach((med) => {
            if (med.id) {
              this.applyMedicationConflicts(med, med.id);
            }
          });

          const existingIds = new Set(
            medicationsItem.value.filter((med) => med.id).map((med) => String(med.id)),
          );
          const existingNames = new Set(
            medicationsItem.value
              .filter((med) => med.Medication && med.Medication.trim())
              .map((med) => med.Medication.toLowerCase().trim()),
          );

          const newMedications = data.medications.filter((medication) => {
            const medicationId = String(medication.Id || '');
            const medName = (medication.MedicationName || '').trim();
            const medDate =
              medication.StartDate &&
              !this.dateValidationService.isInvalidNetDate(medication.StartDate)
                ? medication.StartDate.split('T')[0]
                : '';
            const medStop =
              medication.StopDate &&
              !this.dateValidationService.isInvalidNetDate(medication.StopDate)
                ? medication.StopDate.split('T')[0]
                : '';
            const medFreq = (medication.Freq || '').trim();
            const medDosage = (medication.Dosage || '').trim();

            if (medicationId && existingIds.has(medicationId)) {
              return false;
            }

            const conflictKey = `Medications - Name-${medicationId}`;
            if (this.conflictingFields.has(conflictKey)) {
              const conflict = this.conflictingFields.get(conflictKey);
              if (conflict.saved === 'none') {
                return false;
              }
            }

            const hasExactMatch = medicationsItem.value.some((med) => {
              const existingName = (med.Medication || '').trim();
              const existingDate = (med.Date || '').trim();
              const existingStop = (med.Stop || '').trim();
              const existingFreq = (med.Freq || '').trim();
              const existingDosage = (med.Dosage || '').trim();

              return (
                existingName === medName &&
                existingDate === medDate &&
                existingStop === medStop &&
                existingFreq === medFreq &&
                existingDosage === medDosage
              );
            });

            if (hasExactMatch) {
              return false;
            }

            return true;
          });

          if (newMedications.length > 0) {
            const newMedicationRows = newMedications.map((medication) => {
              const medicationId = medication.Id;
              const conflictKey = `medications-${medicationId}`;
              const hasConflict = this.conflictingFields.has(conflictKey);

              return {
                Date:
                  medication.StartDate &&
                  !this.dateValidationService.isInvalidNetDate(medication.StartDate)
                    ? medication.StartDate.split('T')[0]
                    : '',
                Medication: hasConflict
                  ? this.conflictingFields.get(conflictKey).saved
                  : medication.MedicationName || '',
                Freq: medication.Freq || '',
                Dosage: medication.Dosage || '',
                Stop:
                  medication.StopDate &&
                  !this.dateValidationService.isInvalidNetDate(medication.StopDate)
                    ? medication.StopDate.split('T')[0]
                    : '',
                id: medicationId,
                hasConflict: hasConflict,
                conflictTooltip: hasConflict
                  ? this.buildConflictTooltip(this.conflictingFields.get(conflictKey))
                  : undefined,
              };
            });

            medicationsItem.value = [...medicationsItem.value, ...newMedicationRows];
          }
        }
      }
    }

    this.forceDataUpdate();
  }

  private forceDataUpdate(): void {
    if (!this.payload?.properties?.items?.value) {
      this.ready.next(true);
      return;
    }

    const items = this.payload.properties.items.value;

    items.forEach((item) => {
      if (item.value && Array.isArray(item.value)) {
        // Use JSON.parse(JSON.stringify()) for deep cloning works for past 8 years
        item.value = JSON.parse(JSON.stringify(item.value));
      }
    });

    this.payload = { ...this.payload };

    this.cd.detectChanges();
    this.ready.next(true);
  }

  private prefillFromMetadata(medicalHistoryMetadata: any) {
    if (!this.payload?.properties?.items?.value || !medicalHistoryMetadata) {
      return;
    }

    const items = this.payload.properties.items.value;

    if (medicalHistoryMetadata.surgeries && Array.isArray(medicalHistoryMetadata.surgeries)) {
      const surgeriesItem = items.find((item) => item.name === 'surgeries');
      if (surgeriesItem) {
        const nextRows = medicalHistoryMetadata.surgeries.map((surgery) => {
          return [
            {
              title: 'Diagnose Date',
              value: surgery.diagnose_date || '',
              type: 'date',
              required: true,
            },
            {
              title: 'Surgery',
              value: surgery.name || '',
              placeholder: 'Enter surgeries',
              required: true,
              id: surgery.id || undefined,
            },
          ];
        });
        surgeriesItem.value = preserveInputListScribeMarkers(surgeriesItem.value, nextRows);
      }
    }

    if (
      medicalHistoryMetadata.other_chronic_conditions &&
      Array.isArray(medicalHistoryMetadata.other_chronic_conditions)
    ) {
      const otherChronicItem = items.find((item) => item.name === 'other_chronic_conditions');
      if (otherChronicItem) {
        const nextRows = medicalHistoryMetadata.other_chronic_conditions.map((condition) => [
          {
            title: 'Diagnose Date',
            value: condition.diagnose_date || '',
            type: 'date',
            required: true,
          },
          {
            title: 'Condition',
            value: condition.name || '',
            placeholder: 'Enter other conditions',
            required: true,
            id: condition.id || undefined,
          },
        ]);
        otherChronicItem.value = preserveInputListScribeMarkers(otherChronicItem.value, nextRows);
      }
    }

    if (medicalHistoryMetadata.allergies && Array.isArray(medicalHistoryMetadata.allergies)) {
      const allergiesItem = items.find((item) => item.name === 'allergies');
      if (allergiesItem) {
        const nextRows = medicalHistoryMetadata.allergies.map((allergy) => [
          {
            title: 'Diagnose Date',
            value: allergy.diagnose_date || '',
            type: 'date',
            required: true,
          },
          {
            title: 'Allergy',
            value: allergy.name || '',
            placeholder: 'Enter allergies',
            required: true,
            id: allergy.id || undefined,
          },
        ]);
        allergiesItem.value = preserveInputListScribeMarkers(allergiesItem.value, nextRows);
      }
    }

    if (medicalHistoryMetadata.medications && Array.isArray(medicalHistoryMetadata.medications)) {
      const medicationsItem = items.find((item) => item.name === 'medications');
      if (medicationsItem) {
        const nextRows = medicalHistoryMetadata.medications.map((medication) => ({
          Date: medication.start || medication.diagnose_date || '',
          Medication: medication.name || medication.medication || '',
          Freq: medication.frequency || '',
          Dosage: medication.dosage || '',
          Stop: medication.stop || '',
          id: medication.id || undefined,
        }));
        medicationsItem.value = preserveMedicationScribeMarkers(medicationsItem.value, nextRows);
      }
    }

    if (
      medicalHistoryMetadata.chronic_conditions &&
      Array.isArray(medicalHistoryMetadata.chronic_conditions)
    ) {
      const chronicConditionsItem = items.find((item) => item.name === 'chronic_conditions');
      if (chronicConditionsItem && chronicConditionsItem.items) {
        medicalHistoryMetadata.chronic_conditions.forEach((savedCondition) => {
          const checkboxItem = chronicConditionsItem.items.find(
            (item) => item.title === savedCondition.name,
          );
          if (checkboxItem) {
            const nextValue = normalizeVisibility(savedCondition.checked, false);
            const valueChanged = checkboxItem.value !== nextValue;
            checkboxItem.value = nextValue;
            if (valueChanged) {
              clearScribeSuggestionMarker(checkboxItem);
            }
            if (savedCondition.id) {
              checkboxItem.id = savedCondition.id;
            }
          }
        });
      }
    }

    this.ready.next(true);
  }

  override isValid() {
    return new FbEmrMedicalHistoryWidget(this.payload).isValid();
  }

  hasVisibleCategories(): boolean {
    if (!this.payload.properties.items?.value) {
      return false;
    }

    return this.payload.properties.items.value.some((category) => {
      return this.isCategoryVisible(category) && category.value;
    });
  }

  isCategoryVisible(category: any): boolean {
    const fallbackVisibility = typeof category?.value === 'boolean' ? category.value : true;
    return normalizeVisibility(category?.visible, fallbackVisibility);
  }

  isFirstVisibleCategory(index: number): boolean {
    const categories = this.payload?.properties?.items?.value;
    if (!Array.isArray(categories)) {
      return false;
    }

    for (let i = 0; i < categories.length; i += 1) {
      const category = categories[i];
      if (this.isCategoryVisible(category) && category?.value) {
        return i === index;
      }
    }

    return false;
  }

  private normalizeCategoryVisibility(item: any): void {
    if (typeof item.value === 'boolean') {
      // Legacy templates store category toggle state in `value`.
      item.visible = item.value;
    } else {
      item.visible = normalizeVisibility(item.visible, true);
    }

    if (!Array.isArray(item.items)) {
      return;
    }

    item.items.forEach((nestedItem: any) => {
      if (nestedItem && typeof nestedItem === 'object' && 'visible' in nestedItem) {
        nestedItem.visible = normalizeVisibility(nestedItem.visible, true);
      }
    });
  }

  onAddItem(categoryName: string) {
    switch (categoryName) {
      case 'other_chronic_conditions':
        this.openChronicConditionDialog();
        break;
      case 'allergies':
        this.openAllergyDialog();
        break;
      case 'medications':
        this.openMedicationDialog();
        break;
      case 'surgeries':
        this.openSurgeryDialog();
        break;
    }
  }

  private openChronicConditionDialog() {
    const dialogRef = this.dialog.open(AddChronicConditionDialogComponent, {
      width: '600px',
      data: {
        diagnoseDate: '',
        condition: '',
        widgetApiService: this.widgetApiService,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.addChronicCondition(result);
      }
      this.cd.detectChanges();
    });
  }

  private openAllergyDialog() {
    const dialogRef = this.dialog.open(AddAllergyDialogComponent, {
      width: '600px',
      data: {
        diagnoseDate: '',
        allergy: '',
        widgetApiService: this.widgetApiService,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.addAllergy(result);
      }
      this.cd.detectChanges();
    });
  }

  private openMedicationDialog() {
    const dialogRef = this.dialog.open(AddMedicationDialogComponent, {
      width: '600px',
      data: {
        date: '',
        medication: '',
        frequency: '',
        dosage: '',
        stopDate: '',
        widgetApiService: this.widgetApiService,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.addMedication(result);
      }
      this.cd.detectChanges();
    });
  }

  private openSurgeryDialog() {
    const dialogRef = this.dialog.open(AddSurgeryDialogComponent, {
      width: '600px',
      data: {
        diagnoseDate: '',
        surgery: '',
        widgetApiService: this.widgetApiService,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.addSurgery(result);
      }
      this.cd.detectChanges();
    });
  }

  private addChronicCondition(data: any) {
    const items = this.payload.properties.items.value;
    const itemIndex = items.findIndex((item) => item.name === 'other_chronic_conditions');

    if (itemIndex !== -1) {
      if (!items[itemIndex].value) {
        items[itemIndex].value = [];
      }

      const newEntry = [
        {
          title: 'Diagnose Date',
          value: data.diagnoseDate,
          type: 'date',
          required: true,
        },
        {
          title: 'Condition',
          value: data.condition,
          placeholder: 'Enter other conditions',
          required: true,
        },
      ];

      if (
        items[itemIndex].value.length === 1 &&
        this.isInputListRowEmpty(items[itemIndex].value[0])
      ) {
        items[itemIndex].value[0] = newEntry;
      } else {
        items[itemIndex].value.push(newEntry);
      }

      items[itemIndex].value = [...items[itemIndex].value];

      this.cd.markForCheck();
      this.cd.detectChanges();
      this.ready.next(true);
    }
  }

  private addAllergy(data: any) {
    const items = this.payload.properties.items.value;
    const itemIndex = items.findIndex((item) => item.name === 'allergies');

    if (itemIndex !== -1) {
      if (!items[itemIndex].value) {
        items[itemIndex].value = [];
      }

      const newEntry = [
        {
          title: 'Diagnose Date',
          value: data.diagnoseDate,
          type: 'date',
          required: true,
        },
        {
          title: 'Allergy',
          value: data.allergy,
          placeholder: 'Enter allergies',
          required: true,
        },
      ];

      if (
        items[itemIndex].value.length === 1 &&
        this.isInputListRowEmpty(items[itemIndex].value[0])
      ) {
        items[itemIndex].value[0] = newEntry;
      } else {
        items[itemIndex].value.push(newEntry);
      }

      items[itemIndex].value = [...items[itemIndex].value];

      this.cd.markForCheck();
      this.cd.detectChanges();
      this.ready.next(true);
    }
  }

  private addMedication(data: any) {
    const items = this.payload.properties.items.value;
    const itemIndex = items.findIndex((item) => item.name === 'medications');

    if (itemIndex !== -1) {
      if (!items[itemIndex].value) {
        items[itemIndex].value = [];
      }

      const newEntry = {
        Date: data.date,
        Medication: data.medication,
        Freq: data.frequency,
        Dosage: data.dosage,
        Stop: data.stopDate || '',
      };

      if (items[itemIndex].value.length === 1 && this.isTableRowEmpty(items[itemIndex].value[0])) {
        items[itemIndex].value[0] = newEntry;
      } else {
        items[itemIndex].value.push(newEntry);
      }

      items[itemIndex].value = [...items[itemIndex].value];

      this.cd.markForCheck();
      this.cd.detectChanges();
      this.ready.next(true);
    }
  }

  private addSurgery(data: any) {
    const items = this.payload.properties.items.value;
    const itemIndex = items.findIndex((item) => item.name === 'surgeries');

    if (itemIndex !== -1) {
      if (!items[itemIndex].value) {
        items[itemIndex].value = [];
      }

      const newEntry = [
        {
          title: 'Diagnose Date',
          value: data.diagnoseDate,
          type: 'date',
          required: true,
        },
        {
          title: 'Surgery',
          value: data.surgery,
          placeholder: 'Enter surgeries',
          required: true,
        },
      ];

      if (
        items[itemIndex].value.length === 1 &&
        this.isInputListRowEmpty(items[itemIndex].value[0])
      ) {
        items[itemIndex].value[0] = newEntry;
      } else {
        items[itemIndex].value.push(newEntry);
      }

      items[itemIndex].value = [...items[itemIndex].value];

      this.cd.markForCheck();
      this.cd.detectChanges();
      this.ready.next(true);
    }
  }

  private isInputListRowEmpty(row: any): boolean {
    if (!row || !Array.isArray(row)) return true;
    return row.every((field) => !field.value || field.value === '');
  }

  private isTableRowEmpty(row: any): boolean {
    if (!row || typeof row !== 'object') return true;
    return Object.values(row).every((value) => !value || value === '');
  }

  private formatDate(dateValue: string | Date): string {
    if (!dateValue) return '';

    if (this.dateValidationService.shouldExcludeDate(dateValue)) {
      return '';
    }

    return parseLocalDateString(dateValue);
  }

  isSubmissionPending(): boolean {
    const form = this.entity as any;
    return form?.status === 2 && form?.shared_with_patient === true;
  }

  private buildConflictTooltip(conflict: any): string {
    if (!conflict) return '';

    const parts: string[] = [];

    if (conflict.saved !== conflict.current) {
      parts.push(`Name: "${conflict.saved}" → "${conflict.current}"`);
    }

    if (conflict.savedDate && conflict.currentDate && conflict.savedDate !== conflict.currentDate) {
      parts.push(`Date: ${conflict.savedDate} → ${conflict.currentDate}`);
    }

    if (
      conflict.savedStopDate &&
      conflict.currentStopDate &&
      conflict.savedStopDate !== conflict.currentStopDate
    ) {
      parts.push(`Stop Date: ${conflict.savedStopDate} → ${conflict.currentStopDate}`);
    }
    if (
      conflict.savedFreq !== undefined &&
      conflict.currentFreq !== undefined &&
      conflict.savedFreq !== conflict.currentFreq
    ) {
      parts.push(`Frequency: "${conflict.savedFreq}" → "${conflict.currentFreq}"`);
    }
    if (
      conflict.savedDosage !== undefined &&
      conflict.currentDosage !== undefined &&
      conflict.savedDosage !== conflict.currentDosage
    ) {
      parts.push(`Dosage: "${conflict.savedDosage}" → "${conflict.currentDosage}"`);
    }

    return parts.length > 0 ? `PNE values differ:\n${parts.join('\n')}` : '';
  }

  private detectDiscrepanciesFromMetadata(
    medicalHistoryMetadata: any,
    currentMedicalHistory: any,
  ): any[] {
    const discrepancies: any[] = [];

    if (!medicalHistoryMetadata || !currentMedicalHistory) {
      return discrepancies;
    }

    this.conflictingFields.clear();

    const templateItems = this.payload?.properties?.items?.value || [];
    const chronicEnabled = this.isCategoryEnabledForDiscrepancy(
      templateItems,
      'chronic_conditions',
    );
    const otherChronicEnabled = this.isCategoryEnabledForDiscrepancy(
      templateItems,
      'other_chronic_conditions',
    );
    const allergiesEnabled = this.isCategoryEnabledForDiscrepancy(templateItems, 'allergies');
    const medicationsEnabled = this.isCategoryEnabledForDiscrepancy(templateItems, 'medications');
    const surgeriesEnabled = this.isCategoryEnabledForDiscrepancy(templateItems, 'surgeries');
    const visibleChronicConditionNames = this.getVisibleChronicConditionNames(templateItems);

    const checkboxConditions = medicalHistoryMetadata.chronic_conditions || [];
    const otherConditions = medicalHistoryMetadata.other_chronic_conditions || [];
    const filteredCheckboxConditions =
      chronicEnabled && visibleChronicConditionNames.size > 0
        ? checkboxConditions.filter((condition: any) =>
            visibleChronicConditionNames.has(
              String(condition?.name || '')
                .toLowerCase()
                .trim(),
            ),
          )
        : chronicEnabled
          ? checkboxConditions
          : [];
    const filteredOtherConditions = otherChronicEnabled ? otherConditions : [];

    const allMetadataConditions = [...filteredOtherConditions, ...filteredCheckboxConditions];

    if (chronicEnabled || otherChronicEnabled) {
      this.checkChronicConditionsFromMetadata(
        allMetadataConditions,
        currentMedicalHistory.chronicConditions || [],
        discrepancies,
        chronicEnabled,
        visibleChronicConditionNames,
      );
    }
    if (allergiesEnabled) {
      this.checkAllergiesFromMetadata(
        medicalHistoryMetadata.allergies || [],
        currentMedicalHistory.allergies || [],
        discrepancies,
      );
    }
    if (medicationsEnabled) {
      this.checkMedicationsFromMetadata(
        medicalHistoryMetadata.medications || [],
        currentMedicalHistory.medications || [],
        discrepancies,
      );
    }
    if (surgeriesEnabled) {
      this.checkSurgeriesFromMetadata(
        medicalHistoryMetadata.surgeries || [],
        currentMedicalHistory.surgeries || [],
        discrepancies,
      );
    }

    this.logComparisonTableFromMetadata(medicalHistoryMetadata, currentMedicalHistory);

    return discrepancies;
  }
  /**
   * Public method to detect discrepancies for the presenter component
   * Similar to Insurance widget's implementation
   */

  public detectDiscrepancies(savedFormData: any, currentMedicalHistory: any): any[] {
    const discrepancies: any[] = [];

    if (!savedFormData || !currentMedicalHistory) {
      return discrepancies;
    }

    const savedMedicalHistoryWidget = this.findMedicalHistoryWidget(savedFormData);

    if (!savedMedicalHistoryWidget) {
      return discrepancies;
    }

    const savedItems = savedMedicalHistoryWidget.properties?.items?.value || [];

    const hasAnySavedData = savedItems.some((item) => {
      const value = item.value;
      if (Array.isArray(value)) {
        return value.some((v) => {
          if (typeof v === 'object' && v !== null) {
            return Object.values(v).some((val) => val && val !== '');
          }
          return v && v !== '';
        });
      }
      return value && value !== '' && value !== false;
    });

    if (!hasAnySavedData) {
      return [];
    }

    if (this.isCategoryEnabledForDiscrepancy(savedItems, 'other_chronic_conditions')) {
      this.checkChronicConditions(
        savedItems,
        currentMedicalHistory.chronicConditions,
        discrepancies,
      );
    }
    if (this.isCategoryEnabledForDiscrepancy(savedItems, 'allergies')) {
      this.checkAllergies(savedItems, currentMedicalHistory.allergies, discrepancies);
    }
    if (this.isCategoryEnabledForDiscrepancy(savedItems, 'medications')) {
      this.checkMedications(savedItems, currentMedicalHistory.medications, discrepancies);
    }
    if (this.isCategoryEnabledForDiscrepancy(savedItems, 'surgeries')) {
      this.checkSurgeries(savedItems, currentMedicalHistory.surgeries, discrepancies);
    }

    this.logComparisonTable(savedItems, currentMedicalHistory);

    return discrepancies;
  }

  private logComparisonTable(savedItems: any[], currentMedicalHistory: any): void {
    const comparisonData = [];

    const savedConditionsItem = savedItems.find((item) => item.name === 'other_chronic_conditions');
    const savedConditions = savedConditionsItem?.value || [];
    const savedConditionMap = new Map<string, any>();
    const savedConditionsWithoutId = [];

    if (Array.isArray(savedConditions)) {
      savedConditions.forEach((condition) => {
        const conditionField = condition?.[1];
        const conditionId = conditionField?.id;
        const conditionName = conditionField?.value;
        const conditionDate = parseLocalDateString(condition?.[0]?.value);

        if (conditionId) {
          savedConditionMap.set(String(conditionId), {
            name: conditionName,
            date: conditionDate,
          });
        } else {
          savedConditionsWithoutId.push({
            name: conditionName,
            date: conditionDate,
          });
        }
      });
    }

    const apiConditions = currentMedicalHistory.chronicConditions || [];
    const processedConditionIds = new Set<string>();

    apiConditions.forEach((apiCondition) => {
      const id = String(apiCondition.Id);
      processedConditionIds.add(id);
      const savedCondition = savedConditionMap.get(id);

      comparisonData.push({
        Category: 'Chronic Condition',
        ID: id,
        Field: 'Name',
        'Form Value': savedCondition?.name || 'none',
        'PatientNow Value': apiCondition.ChronicName || 'none',
      });

      comparisonData.push({
        Category: 'Chronic Condition',
        ID: id,
        Field: 'Date',
        'Form Value': savedCondition?.date || 'none',
        'PatientNow Value': parseLocalDateString(apiCondition.OnsetDate) || 'none',
      });
    });

    savedConditionMap.forEach((savedCondition, id) => {
      if (!processedConditionIds.has(id)) {
        comparisonData.push({
          Category: 'Chronic Condition',
          ID: id,
          Field: 'Name',
          'Form Value': savedCondition.name || 'none',
          'PatientNow Value': 'none',
        });

        comparisonData.push({
          Category: 'Chronic Condition',
          ID: id,
          Field: 'Date',
          'Form Value': savedCondition.date || 'none',
          'PatientNow Value': 'none',
        });
      }
    });

    savedConditionsWithoutId.forEach((savedCondition, index) => {
      comparisonData.push({
        Category: 'Chronic Condition',
        ID: `NO_ID_${index + 1}`,
        Field: 'Name',
        'Form Value': savedCondition.name || 'none',
        'PatientNow Value': 'none',
      });

      comparisonData.push({
        Category: 'Chronic Condition',
        ID: `NO_ID_${index + 1}`,
        Field: 'Date',
        'Form Value': savedCondition.date || 'none',
        'PatientNow Value': 'none',
      });
    });

    const savedAllergiesItem = savedItems.find((item) => item.name === 'allergies');
    const savedAllergies = savedAllergiesItem?.value || [];
    const savedAllergyMap = new Map<string, any>();
    const savedAllergiesWithoutId = [];

    if (Array.isArray(savedAllergies)) {
      savedAllergies.forEach((allergy) => {
        const [dateField, allergyField] = allergy || [];
        const allergyId = allergyField?.id;
        const allergyName = allergyField?.value;
        const allergyDate = parseLocalDateString(dateField?.value);

        if (allergyId) {
          savedAllergyMap.set(String(allergyId), {
            name: allergyName,
            date: allergyDate,
          });
        } else {
          savedAllergiesWithoutId.push({
            name: allergyName,
            date: allergyDate,
          });
        }
      });
    }

    const apiAllergies = currentMedicalHistory.allergies || [];
    const processedAllergyIds = new Set<string>();

    apiAllergies.forEach((apiAllergy) => {
      const id = String(apiAllergy.Id);
      processedAllergyIds.add(id);
      const savedAllergy = savedAllergyMap.get(id);

      comparisonData.push({
        Category: 'Allergy',
        ID: id,
        Field: 'Name',
        'Form Value': savedAllergy?.name || 'none',
        'PatientNow Value': apiAllergy.AllergyName || 'none',
      });

      comparisonData.push({
        Category: 'Allergy',
        ID: id,
        Field: 'Date',
        'Form Value': savedAllergy?.date || 'none',
        'PatientNow Value': parseLocalDateString(apiAllergy.DiagDate) || 'none',
      });
    });

    savedAllergyMap.forEach((savedAllergy, id) => {
      if (!processedAllergyIds.has(id)) {
        comparisonData.push({
          Category: 'Allergy',
          ID: id,
          Field: 'Name',
          'Form Value': savedAllergy.name || 'none',
          'PatientNow Value': 'none',
        });

        comparisonData.push({
          Category: 'Allergy',
          ID: id,
          Field: 'Date',
          'Form Value': savedAllergy.date || 'none',
          'PatientNow Value': 'none',
        });
      }
    });

    savedAllergiesWithoutId.forEach((savedAllergy, index) => {
      comparisonData.push({
        Category: 'Allergy',
        ID: `NO_ID_${index + 1}`,
        Field: 'Name',
        'Form Value': savedAllergy.name || 'none',
        'PatientNow Value': 'none',
      });

      comparisonData.push({
        Category: 'Allergy',
        ID: `NO_ID_${index + 1}`,
        Field: 'Date',
        'Form Value': savedAllergy.date || 'none',
        'PatientNow Value': 'none',
      });
    });

    const savedMedicationsItem = savedItems.find((item) => item.name === 'medications');
    const savedMedications = savedMedicationsItem?.value || [];
    const savedMedicationMap = new Map<string, any>();
    const savedMedicationsWithoutId = [];

    if (Array.isArray(savedMedications)) {
      savedMedications.forEach((medication) => {
        const medicationId = medication?.id || medication?.Id;
        const medicationName = medication?.Medication || medication?.MedicationName || '';
        const startDate = parseLocalDateString(medication?.Date || medication?.StartDate);
        const stopDate = parseLocalDateString(medication?.Stop || medication?.StopDate);
        const freq = medication?.Freq || medication?.frequency || '';
        const dosage = medication?.Dosage || medication?.dosage || '';

        if (medicationId) {
          savedMedicationMap.set(String(medicationId), {
            name: medicationName,
            startDate,
            stopDate,
            freq,
            dosage,
          });
        } else {
          savedMedicationsWithoutId.push({
            name: medicationName,
            startDate,
            stopDate,
            freq,
            dosage,
          });
        }
      });
    }

    const apiMedications = currentMedicalHistory.medications || [];
    const processedMedicationIds = new Set<string>();

    apiMedications.forEach((apiMedication) => {
      const id = String(apiMedication.Id);
      processedMedicationIds.add(id);
      const savedMedication = savedMedicationMap.get(id);

      comparisonData.push({
        Category: 'Medication',
        ID: id,
        Field: 'Name',
        'Form Value': savedMedication?.name || 'none',
        'PatientNow Value': apiMedication.MedicationName || 'none',
      });

      comparisonData.push({
        Category: 'Medication',
        ID: id,
        Field: 'Start Date',
        'Form Value': savedMedication?.startDate || 'none',
        'PatientNow Value': parseLocalDateString(apiMedication.StartDate) || 'none',
      });

      comparisonData.push({
        Category: 'Medication',
        ID: id,
        Field: 'Stop Date',
        'Form Value': savedMedication?.stopDate || 'none',
        'PatientNow Value': parseLocalDateString(apiMedication.StopDate) || 'none',
      });

      comparisonData.push({
        Category: 'Medication',
        ID: id,
        Field: 'Frequency',
        'Form Value': savedMedication?.freq || 'none',
        'PatientNow Value': apiMedication.Freq || 'none',
      });

      comparisonData.push({
        Category: 'Medication',
        ID: id,
        Field: 'Dosage',
        'Form Value': savedMedication?.dosage || 'none',
        'PatientNow Value': apiMedication.Dosage || 'none',
      });
    });

    savedMedicationMap.forEach((savedMedication, id) => {
      if (!processedMedicationIds.has(id)) {
        comparisonData.push({
          Category: 'Medication',
          ID: id,
          Field: 'Name',
          'Form Value': savedMedication.name || 'none',
          'PatientNow Value': 'none',
        });

        comparisonData.push({
          Category: 'Medication',
          ID: id,
          Field: 'Start Date',
          'Form Value': savedMedication.startDate || 'none',
          'PatientNow Value': 'none',
        });

        comparisonData.push({
          Category: 'Medication',
          ID: id,
          Field: 'Stop Date',
          'Form Value': savedMedication.stopDate || 'none',
          'PatientNow Value': 'none',
        });

        comparisonData.push({
          Category: 'Medication',
          ID: id,
          Field: 'Frequency',
          'Form Value': savedMedication.freq || 'none',
          'PatientNow Value': 'none',
        });

        comparisonData.push({
          Category: 'Medication',
          ID: id,
          Field: 'Dosage',
          'Form Value': savedMedication.dosage || 'none',
          'PatientNow Value': 'none',
        });
      }
    });

    savedMedicationsWithoutId.forEach((savedMedication, index) => {
      comparisonData.push({
        Category: 'Medication',
        ID: `NO_ID_${index + 1}`,
        Field: 'Name',
        'Form Value': savedMedication.name || 'none',
        'PatientNow Value': 'none',
      });

      comparisonData.push({
        Category: 'Medication',
        ID: `NO_ID_${index + 1}`,
        Field: 'Start Date',
        'Form Value': savedMedication.startDate || 'none',
        'PatientNow Value': 'none',
      });

      comparisonData.push({
        Category: 'Medication',
        ID: `NO_ID_${index + 1}`,
        Field: 'Stop Date',
        'Form Value': savedMedication.stopDate || 'none',
        'PatientNow Value': 'none',
      });

      comparisonData.push({
        Category: 'Medication',
        ID: `NO_ID_${index + 1}`,
        Field: 'Frequency',
        'Form Value': savedMedication.freq || 'none',
        'PatientNow Value': 'none',
      });

      comparisonData.push({
        Category: 'Medication',
        ID: `NO_ID_${index + 1}`,
        Field: 'Dosage',
        'Form Value': savedMedication.dosage || 'none',
        'PatientNow Value': 'none',
      });
    });

    const savedSurgeriesItem = savedItems.find((item) => item.name === 'surgeries');
    const savedSurgeries = savedSurgeriesItem?.value || [];
    const savedSurgeryMap = new Map<string, any>();
    const savedSurgeriesWithoutId = [];

    if (Array.isArray(savedSurgeries)) {
      savedSurgeries.forEach((surgery) => {
        const surgeryId = surgery?.id || surgery?.[1]?.id;
        const surgeryName = surgery?.name || surgery?.[1]?.value;
        const surgeryDate = parseLocalDateString(surgery?.diagnose_date || surgery?.[0]?.value);

        if (surgeryId) {
          savedSurgeryMap.set(String(surgeryId), {
            name: surgeryName,
            date: surgeryDate,
          });
        } else {
          savedSurgeriesWithoutId.push({
            name: surgeryName,
            date: surgeryDate,
          });
        }
      });
    }

    const apiSurgeries = currentMedicalHistory.surgeries || [];
    const processedSurgeryIds = new Set<string>();

    apiSurgeries.forEach((apiSurgery) => {
      const id = String(apiSurgery.Id);
      processedSurgeryIds.add(id);
      const savedSurgery = savedSurgeryMap.get(id);

      comparisonData.push({
        Category: 'Surgery',
        ID: id,
        Field: 'Name',
        'Form Value': savedSurgery?.name || 'none',
        'PatientNow Value': apiSurgery.SurgeryName || 'none',
      });

      comparisonData.push({
        Category: 'Surgery',
        ID: id,
        Field: 'Date',
        'Form Value': savedSurgery?.date || 'none',
        'PatientNow Value': parseLocalDateString(apiSurgery.SurgeryDate) || 'none',
      });
    });

    savedSurgeryMap.forEach((savedSurgery, id) => {
      if (!processedSurgeryIds.has(id)) {
        comparisonData.push({
          Category: 'Surgery',
          ID: id,
          Field: 'Name',
          'Form Value': savedSurgery.name || 'none',
          'PatientNow Value': 'none',
        });

        comparisonData.push({
          Category: 'Surgery',
          ID: id,
          Field: 'Date',
          'Form Value': savedSurgery.date || 'none',
          'PatientNow Value': 'none',
        });
      }
    });

    savedSurgeriesWithoutId.forEach((savedSurgery, index) => {
      comparisonData.push({
        Category: 'Surgery',
        ID: `NO_ID_${index + 1}`,
        Field: 'Name',
        'Form Value': savedSurgery.name || 'none',
        'PatientNow Value': 'none',
      });

      comparisonData.push({
        Category: 'Surgery',
        ID: `NO_ID_${index + 1}`,
        Field: 'Date',
        'Form Value': savedSurgery.date || 'none',
        'PatientNow Value': 'none',
      });
    });

    console.log('✌️ Medical History: Comparison Table - Form Data vs PNE Data');
    console.table(comparisonData);
  }

  private logComparisonTableFromMetadata(metadata: any, currentMedicalHistory: any): void {
    const comparisonData = [];

    const metadataConditions = [
      ...(metadata.other_chronic_conditions || []),
      ...(metadata.chronic_conditions || []),
    ];
    const metadataConditionMap = new Map<string, any>();
    const metadataConditionsWithoutId = [];

    metadataConditions.forEach((condition) => {
      if (condition.id) {
        metadataConditionMap.set(String(condition.id), {
          name: condition.name,
          date: condition.diagnose_date,
        });
      } else {
        metadataConditionsWithoutId.push({
          name: condition.name,
          date: condition.diagnose_date,
        });
      }
    });

    const apiConditions = currentMedicalHistory.chronicConditions || [];
    const processedConditionIds = new Set<string>();

    apiConditions.forEach((apiCondition) => {
      const id = String(apiCondition.Id);
      processedConditionIds.add(id);
      const metadataCondition = metadataConditionMap.get(id);

      comparisonData.push(
        {
          Category: 'Chronic Condition',
          ID: id,
          Field: 'Name',
          'Form Value': metadataCondition?.name || 'none',
          'PatientNow Value': apiCondition.ChronicName || 'none',
        },
        {
          Category: 'Chronic Condition',
          ID: id,
          Field: 'Date',
          'Form Value': metadataCondition?.date || 'none',
          'PatientNow Value':
            parseLocalDateString(apiCondition.OnsetDate || apiCondition.DiagnoseDate) || 'none',
        },
      );
    });

    metadataConditionMap.forEach((condition, id) => {
      if (!processedConditionIds.has(id)) {
        comparisonData.push(
          {
            Category: 'Chronic Condition',
            ID: id,
            Field: 'Name',
            'Form Value': condition.name || 'none',
            'PatientNow Value': 'none',
          },
          {
            Category: 'Chronic Condition',
            ID: id,
            Field: 'Date',
            'Form Value': condition.date || 'none',
            'PatientNow Value': 'none',
          },
        );
      }
    });

    metadataConditionsWithoutId.forEach((condition, index) => {
      comparisonData.push(
        {
          Category: 'Chronic Condition',
          ID: `NO_ID_${index + 1}`,
          Field: 'Name',
          'Form Value': condition.name || 'none',
          'PatientNow Value': 'none',
        },
        {
          Category: 'Chronic Condition',
          ID: `NO_ID_${index + 1}`,
          Field: 'Date',
          'Form Value': condition.date || 'none',
          'PatientNow Value': 'none',
        },
      );
    });

    const metadataAllergies = metadata.allergies || [];
    const metadataAllergyMap = new Map<string, any>();
    const metadataAllergiesWithoutId = [];

    metadataAllergies.forEach((allergy) => {
      if (allergy.id) {
        metadataAllergyMap.set(String(allergy.id), {
          name: allergy.name,
          date: allergy.diagnose_date,
        });
      } else {
        metadataAllergiesWithoutId.push({
          name: allergy.name,
          date: allergy.diagnose_date,
        });
      }
    });

    const apiAllergies = currentMedicalHistory.allergies || [];
    const processedAllergyIds = new Set<string>();

    apiAllergies.forEach((apiAllergy) => {
      const id = String(apiAllergy.Id);
      processedAllergyIds.add(id);
      const metadataAllergy = metadataAllergyMap.get(id);

      comparisonData.push(
        {
          Category: 'Allergy',
          ID: id,
          Field: 'Name',
          'Form Value': metadataAllergy?.name || 'none',
          'PatientNow Value': apiAllergy.AllergyName || 'none',
        },
        {
          Category: 'Allergy',
          ID: id,
          Field: 'Date',
          'Form Value': metadataAllergy?.date || 'none',
          'PatientNow Value': parseLocalDateString(apiAllergy.DiagnoseDate) || 'none',
        },
      );
    });

    metadataAllergyMap.forEach((allergy, id) => {
      if (!processedAllergyIds.has(id)) {
        comparisonData.push(
          {
            Category: 'Allergy',
            ID: id,
            Field: 'Name',
            'Form Value': allergy.name || 'none',
            'PatientNow Value': 'none',
          },
          {
            Category: 'Allergy',
            ID: id,
            Field: 'Date',
            'Form Value': allergy.date || 'none',
            'PatientNow Value': 'none',
          },
        );
      }
    });

    metadataAllergiesWithoutId.forEach((allergy, index) => {
      comparisonData.push(
        {
          Category: 'Allergy',
          ID: `NO_ID_${index + 1}`,
          Field: 'Name',
          'Form Value': allergy.name || 'none',
          'PatientNow Value': 'none',
        },
        {
          Category: 'Allergy',
          ID: `NO_ID_${index + 1}`,
          Field: 'Date',
          'Form Value': allergy.date || 'none',
          'PatientNow Value': 'none',
        },
      );
    });

    const metadataMedications = metadata.medications || [];
    const metadataMedicationMap = new Map<string, any>();
    const metadataMedicationsWithoutId = [];

    metadataMedications.forEach((medication) => {
      if (medication.id) {
        metadataMedicationMap.set(String(medication.id), medication);
      } else {
        metadataMedicationsWithoutId.push(medication);
      }
    });

    const apiMedications = currentMedicalHistory.medications || [];
    const processedMedicationIds = new Set<string>();

    apiMedications.forEach((apiMedication) => {
      const id = String(apiMedication.Id);
      processedMedicationIds.add(id);
      const metadataMedication = metadataMedicationMap.get(id);

      comparisonData.push(
        {
          Category: 'Medication',
          ID: id,
          Field: 'Name',
          'Form Value': metadataMedication?.medication || 'none',
          'PatientNow Value': apiMedication.MedicationName || 'none',
        },
        {
          Category: 'Medication',
          ID: id,
          Field: 'Start Date',
          'Form Value': metadataMedication?.start || 'none',
          'PatientNow Value': parseLocalDateString(apiMedication.StartDate) || 'none',
        },
        {
          Category: 'Medication',
          ID: id,
          Field: 'Stop Date',
          'Form Value': metadataMedication?.stop || 'none',
          'PatientNow Value': parseLocalDateString(apiMedication.StopDate) || 'none',
        },
        {
          Category: 'Medication',
          ID: id,
          Field: 'Frequency',
          'Form Value': metadataMedication?.frequency || 'none',
          'PatientNow Value': apiMedication.Freq || 'none',
        },
        {
          Category: 'Medication',
          ID: id,
          Field: 'Dosage',
          'Form Value': metadataMedication?.dosage || 'none',
          'PatientNow Value': apiMedication.Dosage || 'none',
        },
      );
    });

    metadataMedicationMap.forEach((medication, id) => {
      if (!processedMedicationIds.has(id)) {
        comparisonData.push(
          {
            Category: 'Medication',
            ID: id,
            Field: 'Name',
            'Form Value': medication.medication || 'none',
            'PatientNow Value': 'none',
          },
          {
            Category: 'Medication',
            ID: id,
            Field: 'Start Date',
            'Form Value': medication.start || 'none',
            'PatientNow Value': 'none',
          },
          {
            Category: 'Medication',
            ID: id,
            Field: 'Stop Date',
            'Form Value': medication.stop || 'none',
            'PatientNow Value': 'none',
          },
          {
            Category: 'Medication',
            ID: id,
            Field: 'Frequency',
            'Form Value': medication.frequency || 'none',
            'PatientNow Value': 'none',
          },
          {
            Category: 'Medication',
            ID: id,
            Field: 'Dosage',
            'Form Value': medication.dosage || 'none',
            'PatientNow Value': 'none',
          },
        );
      }
    });

    metadataMedicationsWithoutId.forEach((medication, index) => {
      comparisonData.push(
        {
          Category: 'Medication',
          ID: `NO_ID_${index + 1}`,
          Field: 'Name',
          'Form Value': medication.medication || 'none',
          'PatientNow Value': 'none',
        },
        {
          Category: 'Medication',
          ID: `NO_ID_${index + 1}`,
          Field: 'Start Date',
          'Form Value': medication.start || 'none',
          'PatientNow Value': 'none',
        },
        {
          Category: 'Medication',
          ID: `NO_ID_${index + 1}`,
          Field: 'Stop Date',
          'Form Value': medication.stop || 'none',
          'PatientNow Value': 'none',
        },
        {
          Category: 'Medication',
          ID: `NO_ID_${index + 1}`,
          Field: 'Frequency',
          'Form Value': medication.frequency || 'none',
          'PatientNow Value': 'none',
        },
        {
          Category: 'Medication',
          ID: `NO_ID_${index + 1}`,
          Field: 'Dosage',
          'Form Value': medication.dosage || 'none',
          'PatientNow Value': 'none',
        },
      );
    });

    const metadataSurgeries = metadata.surgeries || [];
    const metadataSurgeryMap = new Map<string, any>();
    const metadataSurgeriesWithoutId = [];

    metadataSurgeries.forEach((surgery) => {
      if (surgery.id) {
        metadataSurgeryMap.set(String(surgery.id), {
          name: surgery.name,
          date: surgery.diagnose_date,
        });
      } else {
        metadataSurgeriesWithoutId.push({
          name: surgery.name,
          date: surgery.diagnose_date,
        });
      }
    });

    const apiSurgeries = currentMedicalHistory.surgeries || [];
    const processedSurgeryIds = new Set<string>();

    apiSurgeries.forEach((apiSurgery) => {
      const id = String(apiSurgery.Id);
      processedSurgeryIds.add(id);
      const metadataSurgery = metadataSurgeryMap.get(id);

      comparisonData.push(
        {
          Category: 'Surgery',
          ID: id,
          Field: 'Name',
          'Form Value': metadataSurgery?.name || 'none',
          'PatientNow Value': apiSurgery.SurgeryName || 'none',
        },
        {
          Category: 'Surgery',
          ID: id,
          Field: 'Date',
          'Form Value': metadataSurgery?.date || 'none',
          'PatientNow Value': parseLocalDateString(apiSurgery.SurgeryDate) || 'none',
        },
      );
    });

    metadataSurgeryMap.forEach((surgery, id) => {
      if (!processedSurgeryIds.has(id)) {
        comparisonData.push(
          {
            Category: 'Surgery',
            ID: id,
            Field: 'Name',
            'Form Value': surgery.name || 'none',
            'PatientNow Value': 'none',
          },
          {
            Category: 'Surgery',
            ID: id,
            Field: 'Date',
            'Form Value': surgery.date || 'none',
            'PatientNow Value': 'none',
          },
        );
      }
    });

    metadataSurgeriesWithoutId.forEach((surgery, index) => {
      comparisonData.push(
        {
          Category: 'Surgery',
          ID: `NO_ID_${index + 1}`,
          Field: 'Name',
          'Form Value': surgery.name || 'none',
          'PatientNow Value': 'none',
        },
        {
          Category: 'Surgery',
          ID: `NO_ID_${index + 1}`,
          Field: 'Date',
          'Form Value': surgery.date || 'none',
          'PatientNow Value': 'none',
        },
      );
    });

    console.log('✌️ Medical History: Comparison Table - Metadata vs PNE Data');
    console.table(comparisonData);
  }

  private checkChronicConditionsFromMetadata(
    metadataConditions: any[],
    apiConditions: any[],
    discrepancies: any[],
    includeApiOnlyComparisons: boolean = true,
    visibleChronicConditionNames: Set<string> = new Set<string>(),
  ) {
    const processedApiIds = new Set<string>();
    const metadataConditionNames = new Set<string>();

    metadataConditions.forEach((metadataCondition) => {
      if (metadataCondition.name) {
        metadataConditionNames.add(metadataCondition.name.toLowerCase().trim());
      }
    });

    metadataConditions.forEach((metadataCondition) => {
      if (!metadataCondition.name) return;

      let apiCondition = metadataCondition.id
        ? apiConditions.find((api) => String(api.Id) === String(metadataCondition.id))
        : null;

      if (!apiCondition) {
        const conditionName = metadataCondition.name.toLowerCase().trim();
        apiCondition = apiConditions.find(
          (api) => api.ChronicName && api.ChronicName.toLowerCase().trim() === conditionName,
        );
      }

      if (!apiCondition) {
        this.conflictingFields.set(
          `other_chronic_conditions-Chronic Condition-${metadataCondition.id}`,
          {
            saved: metadataCondition.name || 'none',
            current: 'none',
          },
        );
        discrepancies.push({
          field: `Chronic Condition - ${metadataCondition.name} - Name`,
          saved: metadataCondition.name || 'none',
          current: 'none',
        });
      } else {
        console.log(`✌️ Metadata condition "${metadataCondition.name}" FOUND in API`);
        if (apiCondition.Id) {
          processedApiIds.add(String(apiCondition.Id));
        }

        const metadataName = metadataCondition.name || '';
        const apiName = apiCondition.ChronicName || apiCondition.Name || '';
        if (metadataName.toLowerCase().trim() !== apiName.toLowerCase().trim()) {
          this.conflictingFields.set(
            `other_chronic_conditions-Chronic Condition Name-${metadataCondition.id}`,
            {
              saved: metadataName,
              current: apiName,
            },
          );
          discrepancies.push({
            field: `Chronic Condition - Name`,
            saved: metadataName,
            current: apiName,
          });
        }

        if (metadataCondition.diagnose_date !== undefined) {
          const metadataDate = parseLocalDateString(metadataCondition.diagnose_date);
          const apiDate = parseLocalDateString(apiCondition.OnsetDate || apiCondition.DiagnoseDate);

          if (metadataDate && apiDate && metadataDate !== apiDate) {
            this.conflictingFields.set(
              `other_chronic_conditions-Chronic Condition Date-${metadataCondition.id}`,
              {
                saved: metadataDate,
                current: apiDate,
              },
            );
            const displayName = apiName || metadataName;
            discrepancies.push({
              field: `Chronic Condition (${displayName}) - Date`,
              saved: metadataDate,
              current: apiDate,
            });
          }
        }
      }
    });

    const reportedConditionNames = new Set<string>();

    if (!includeApiOnlyComparisons) {
      return;
    }

    apiConditions.forEach((apiCondition) => {
      const id = String(apiCondition.Id);
      const conditionName = apiCondition.ChronicName || '';
      const conditionNameLower = conditionName.toLowerCase().trim();

      if (processedApiIds.has(id)) {
        return;
      }

      if (metadataConditionNames.has(conditionNameLower)) {
        return;
      }

      if (
        visibleChronicConditionNames.size > 0 &&
        !visibleChronicConditionNames.has(conditionNameLower)
      ) {
        return;
      }

      if (!reportedConditionNames.has(conditionNameLower)) {
        reportedConditionNames.add(conditionNameLower);
        this.conflictingFields.set(`other_chronic_conditions-Chronic Condition-${id}`, {
          saved: 'none',
          current: conditionName,
        });

        discrepancies.push({
          field: 'Chronic Condition - Name',
          saved: 'none',
          current: conditionName,
        });
      }
    });
  }

  private checkAllergiesFromMetadata(
    metadataAllergies: any[],
    apiAllergies: any[],
    discrepancies: any[],
  ) {
    const processedApiIds = new Set<string>();
    const metadataAllergyNames = new Set<string>();

    metadataAllergies.forEach((metadataAllergy) => {
      if (metadataAllergy.name) {
        metadataAllergyNames.add(metadataAllergy.name.toLowerCase().trim());
      }
    });

    metadataAllergies.forEach((metadataAllergy) => {
      if (!metadataAllergy.name) return;

      let apiAllergy = metadataAllergy.id
        ? apiAllergies.find((api) => String(api.Id) === String(metadataAllergy.id))
        : null;

      if (!apiAllergy) {
        const allergyName = metadataAllergy.name.toLowerCase().trim();
        apiAllergy = apiAllergies.find(
          (api) => api.AllergyName && api.AllergyName.toLowerCase().trim() === allergyName,
        );
      }

      if (!apiAllergy) {
        this.conflictingFields.set(`allergies-Allergy-${metadataAllergy.id}`, {
          saved: metadataAllergy.name || 'none',
          current: 'none',
        });
        discrepancies.push({
          field: `Allergy - ${metadataAllergy.name} - Name`,
          saved: metadataAllergy.name || 'none',
          current: 'none',
        });
      } else {
        if (apiAllergy.Id) {
          processedApiIds.add(String(apiAllergy.Id));
        }

        const metadataName = metadataAllergy.name || '';
        const apiName = apiAllergy.AllergyName || '';
        if (metadataName.toLowerCase().trim() !== apiName.toLowerCase().trim()) {
          this.conflictingFields.set(`allergies-Allergy Name-${metadataAllergy.id}`, {
            saved: metadataName,
            current: apiName,
          });
          discrepancies.push({
            field: `Allergy - Name`,
            saved: metadataName,
            current: apiName,
          });
        }

        const metadataDate = parseLocalDateString(metadataAllergy.diagnose_date);
        const apiDate = parseLocalDateString(apiAllergy.DiagDate);

        if (metadataDate && apiDate && metadataDate !== apiDate) {
          this.conflictingFields.set(`allergies-Allergy Date-${metadataAllergy.id}`, {
            saved: metadataDate,
            current: apiDate,
          });
          const displayName = apiName || metadataName;
          discrepancies.push({
            field: `Allergy (${displayName}) - Date`,
            saved: metadataDate,
            current: apiDate,
          });
        }
      }
    });

    apiAllergies.forEach((apiAllergy) => {
      const id = String(apiAllergy.Id);
      const allergyName = apiAllergy.AllergyName || '';
      const allergyNameLower = allergyName.toLowerCase().trim();

      if (processedApiIds.has(id)) {
        return;
      }

      if (metadataAllergyNames.has(allergyNameLower)) {
        return;
      }

      this.conflictingFields.set(`allergies-Allergy-${id}`, {
        saved: 'none',
        current: apiAllergy.AllergyName || 'none',
      });
      discrepancies.push({
        field: 'Allergy - Name',
        saved: 'none',
        current: apiAllergy.AllergyName || 'none',
      });

      const apiDate = parseLocalDateString(apiAllergy.DiagDate);
      if (apiDate) {
        this.conflictingFields.set(`allergies-Allergy Date-${id}`, {
          saved: 'none',
          current: apiDate,
        });
        discrepancies.push({
          field: `Allergy (${allergyName}) - Date`,
          saved: 'none',
          current: apiDate,
        });
      }
    });
  }

  private checkMedicationsFromMetadata(
    metadataMedications: any[],
    apiMedications: any[],
    discrepancies: any[],
  ) {
    const processedApiIds = new Set<string>();

    metadataMedications.forEach((metadataMed) => {
      if (!metadataMed.id) return;

      const apiMed = apiMedications.find((api) => String(api.Id) === String(metadataMed.id));

      if (!apiMed) {
        this.conflictingFields.set(`Medications - Name-${metadataMed.id}`, {
          saved: metadataMed.medication || 'none',
          current: 'none',
        });
        discrepancies.push({
          field: 'Medication - Name',
          saved: metadataMed.medication || 'none',
          current: 'none',
        });
        return;
      }

      processedApiIds.add(String(apiMed.Id));

      const metadataStartDate = parseLocalDateString(metadataMed.start);
      const apiStartDate = parseLocalDateString(apiMed.StartDate);
      const metadataStopDate = parseLocalDateString(metadataMed.stop);
      const apiStopDate = parseLocalDateString(apiMed.StopDate);

      if (metadataStartDate && apiStartDate && metadataStartDate !== apiStartDate) {
        this.conflictingFields.set(`Medications - Start Date-${metadataMed.id}`, {
          saved: metadataStartDate,
          current: apiStartDate,
        });
        discrepancies.push({
          field: 'Medication - Start Date',
          saved: metadataStartDate,
          current: apiStartDate,
        });
      }

      if (isValidDateForDiscrepancy(metadataStopDate) && isValidDateForDiscrepancy(apiStopDate)) {
        if (metadataStopDate !== apiStopDate) {
          this.conflictingFields.set(`Medications - Stop Date-${metadataMed.id}`, {
            saved: metadataStopDate,
            current: apiStopDate,
          });
          discrepancies.push({
            field: 'Medication - Stop Date',
            saved: metadataStopDate,
            current: apiStopDate,
          });
        }
      }

      const metadataName = metadataMed.medication || '';
      const apiName = apiMed.MedicationName || '';
      if (metadataName && apiName && metadataName !== apiName) {
        this.conflictingFields.set(`Medications - Name-${metadataMed.id}`, {
          saved: metadataName,
          current: apiName,
        });
        discrepancies.push({
          field: 'Medication - Name',
          saved: metadataName,
          current: apiName,
        });
      }

      const metadataFreq = metadataMed.frequency || '';
      const apiFreq = apiMed.Freq || '';
      if (metadataFreq !== apiFreq && (metadataFreq || apiFreq)) {
        this.conflictingFields.set(`Medications - Frequency-${metadataMed.id}`, {
          saved: metadataFreq,
          current: apiFreq,
        });
        discrepancies.push({
          field: 'Medication - Frequency',
          saved: metadataFreq || 'none',
          current: apiFreq || 'none',
        });
      }

      const metadataDosage = metadataMed.dosage || '';
      const apiDosage = apiMed.Dosage || '';
      if (metadataDosage !== apiDosage && (metadataDosage || apiDosage)) {
        this.conflictingFields.set(`Medications - Dosage-${metadataMed.id}`, {
          saved: metadataDosage,
          current: apiDosage,
        });
        discrepancies.push({
          field: 'Medication - Dosage',
          saved: metadataDosage || 'none',
          current: apiDosage || 'none',
        });
      }
    });

    apiMedications.forEach((apiMed) => {
      const id = String(apiMed.Id);
      if (!processedApiIds.has(id)) {
        this.conflictingFields.set(`Medications - Name-${id}`, {
          saved: 'none',
          current: apiMed.MedicationName || 'none',
        });
        discrepancies.push({
          field: 'Medication - Name',
          saved: 'none',
          current: apiMed.MedicationName || 'none',
        });
      }
    });
  }

  private checkSurgeriesFromMetadata(
    metadataSurgeries: any[],
    apiSurgeries: any[],
    discrepancies: any[],
  ) {
    const processedApiIds = new Set<string>();
    const metadataSurgeryNames = new Set<string>();

    metadataSurgeries.forEach((metadataSurgery) => {
      if (metadataSurgery.name) {
        metadataSurgeryNames.add(metadataSurgery.name.toLowerCase().trim());
      }
    });

    metadataSurgeries.forEach((metadataSurgery) => {
      if (!metadataSurgery.name) return;

      let apiSurgery = metadataSurgery.id
        ? apiSurgeries.find((api) => String(api.Id) === String(metadataSurgery.id))
        : null;

      if (!apiSurgery) {
        const surgeryName = metadataSurgery.name.toLowerCase().trim();
        apiSurgery = apiSurgeries.find(
          (api) => api.SurgeryName && api.SurgeryName.toLowerCase().trim() === surgeryName,
        );
      }

      if (!apiSurgery) {
        this.conflictingFields.set(`surgeries-Surgery-${metadataSurgery.id}`, {
          saved: metadataSurgery.name || 'none',
          current: 'none',
        });
        discrepancies.push({
          field: `Surgery - ${metadataSurgery.name} - Name`,
          saved: metadataSurgery.name || 'none',
          current: 'none',
        });
      } else {
        if (apiSurgery.Id) {
          processedApiIds.add(String(apiSurgery.Id));
        }

        const metadataName = metadataSurgery.name || '';
        const apiName = apiSurgery.SurgeryName || '';
        if (metadataName.toLowerCase().trim() !== apiName.toLowerCase().trim()) {
          this.conflictingFields.set(`surgeries-Surgery Name-${metadataSurgery.id}`, {
            saved: metadataName,
            current: apiName,
          });
          discrepancies.push({
            field: `Surgery - Name`,
            saved: metadataName,
            current: apiName,
          });
        }

        const metadataDate = parseLocalDateString(metadataSurgery.diagnose_date);
        const apiDate = parseLocalDateString(apiSurgery.SurgeryDate);

        if (metadataDate && apiDate && metadataDate !== apiDate) {
          this.conflictingFields.set(`surgeries-Surgery Date-${metadataSurgery.id}`, {
            saved: metadataDate,
            current: apiDate,
          });
          const displayName = apiName || metadataName;
          discrepancies.push({
            field: `Surgery (${displayName}) - Date`,
            saved: metadataDate,
            current: apiDate,
          });
        }
      }
    });

    apiSurgeries.forEach((apiSurgery) => {
      const id = String(apiSurgery.Id);
      const surgeryName = apiSurgery.SurgeryName || '';
      const surgeryNameLower = surgeryName.toLowerCase().trim();

      if (processedApiIds.has(id)) {
        return;
      }

      if (metadataSurgeryNames.has(surgeryNameLower)) {
        return;
      }

      this.conflictingFields.set(`surgeries-Surgery-${id}`, {
        saved: 'none',
        current: apiSurgery.SurgeryName || 'none',
      });
      discrepancies.push({
        field: 'Surgery - Name',
        saved: 'none',
        current: apiSurgery.SurgeryName || 'none',
      });

      const apiDate = parseLocalDateString(apiSurgery.SurgeryDate);
      if (apiDate) {
        this.conflictingFields.set(`surgeries-Surgery Date-${id}`, {
          saved: 'none',
          current: apiDate,
        });
        discrepancies.push({
          field: `Surgery (${surgeryName}) - Date`,
          saved: 'none',
          current: apiDate,
        });
      }
    });
  }

  private findMedicalHistoryWidget(formData: any): any {
    if (typeof formData === 'string') {
      try {
        formData = JSON.parse(formData);
      } catch (e) {
        return null;
      }
    }

    if (formData.sections) {
      for (const section of formData.sections) {
        for (const widget of section.widgets || []) {
          if (widget.name === 'fb-emr-medical-history') {
            return widget;
          }
        }
      }
    }

    return null;
  }

  private isCategoryEnabledForDiscrepancy(items: any[], categoryName: string): boolean {
    if (!Array.isArray(items)) {
      return false;
    }

    const category = items.find((item: any) => item?.name === categoryName);
    if (!category) {
      return false;
    }

    const normalizedVisible = normalizeVisibility(category?.visible, true);

    if (typeof category?.value === 'boolean') {
      return normalizedVisible && category.value;
    }

    return normalizedVisible;
  }

  private getVisibleChronicConditionNames(items: any[]): Set<string> {
    const names = new Set<string>();
    if (
      !Array.isArray(items) ||
      !this.isCategoryEnabledForDiscrepancy(items, 'chronic_conditions')
    ) {
      return names;
    }

    const chronicItem = items.find((item: any) => item?.name === 'chronic_conditions');
    if (!chronicItem || !Array.isArray(chronicItem.items)) {
      return names;
    }

    chronicItem.items.forEach((field: any) => {
      if (!field) {
        return;
      }
      if (normalizeVisibility(field.visible, true) && field.title) {
        names.add(String(field.title).toLowerCase().trim());
      }
    });

    return names;
  }

  private checkChronicConditions(
    savedItems: any[],
    currentConditions: any[],
    discrepancies: any[],
  ) {
    const savedConditionsItem = savedItems.find((item) => item.name === 'other_chronic_conditions');
    const savedConditions = savedConditionsItem?.value || [];

    const savedConditionMap = new Map<string, any>();
    const savedConditionsWithoutId = [];

    if (!Array.isArray(savedConditions)) {
      console.warn('savedConditions is not an array:', savedConditions);
      return;
    }

    savedConditions.forEach((condition) => {
      const conditionField = condition?.[1];
      const conditionId = conditionField?.id;

      const parsedDate = parseLocalDateString(condition?.[0]?.value);

      if (conditionId) {
        savedConditionMap.set(String(conditionId), {
          name: conditionField?.value,
          date: parsedDate,
        });
      } else if (conditionField?.value) {
        savedConditionsWithoutId.push({
          name: conditionField?.value,
          date: parsedDate,
        });
      }
    });

    const currentConditionMap = new Map<string, any>();

    currentConditions?.forEach((condition) => {
      const id = condition.Id;

      if (id) {
        currentConditionMap.set(String(id), {
          name: condition.ChronicName,
          date: condition.OnsetDate,
        });
      }
    });

    savedConditionMap.forEach((savedCondition, savedId) => {
      const currentCondition = currentConditionMap.get(savedId);
      if (!currentCondition) {
      } else {
        const nameChanged = savedCondition.name !== currentCondition.name;
        const currentParsedDate = parseLocalDateString(currentCondition.date);
        const dateChanged =
          savedCondition.date && currentParsedDate && savedCondition.date !== currentParsedDate;

        if (nameChanged || dateChanged) {
          let savedDisplay = '';
          let currentDisplay = '';

          if (nameChanged && dateChanged) {
            savedDisplay = savedCondition.date
              ? `${savedCondition.name} | ${savedCondition.date}`
              : savedCondition.name;
            currentDisplay = currentParsedDate
              ? `${currentCondition.name} | ${currentParsedDate}`
              : currentCondition.name;
          } else if (nameChanged) {
            savedDisplay = savedCondition.name || '';
            currentDisplay = currentCondition.name || '';
          } else if (dateChanged) {
            savedDisplay = savedCondition.date || '';
            currentDisplay = currentParsedDate || '';
          }

          discrepancies.push({
            field: `Chronic Condition - ${savedCondition.name} - Date`,
            category: 'other_chronic_conditions',
            savedId: savedId,
            currentId: savedId,
            savedValue: savedDisplay,
            currentValue: currentDisplay,
          });
        }
      }
    });
  }

  private checkAllergies(savedItems: any[], currentAllergies: any[], discrepancies: any[]) {
    const savedAllergiesItem = savedItems.find((item) => item.name === 'allergies');
    const savedAllergies = savedAllergiesItem?.value || [];

    if (!Array.isArray(savedAllergies)) {
      return;
    }

    const savedAllergyMap = new Map<string, any>();

    savedAllergies.forEach((allergy) => {
      const [dateField, allergyField] = allergy || [];
      const allergyId = allergyField?.id;
      const allergyName = allergyField?.value;

      if (allergyId) {
        savedAllergyMap.set(String(allergyId), {
          name: allergyName,
          date: parseLocalDateString(dateField?.value),
        });
      } else if (allergyName) {
      }
    });

    const currentAllergyMap = new Map<string, any>();

    currentAllergies?.forEach((allergy) => {
      const id = allergy.Id;
      if (id) {
        currentAllergyMap.set(String(id), {
          name: allergy.AllergyName,
          date: allergy.DiagDate,
        });
      }
    });

    savedAllergyMap.forEach((savedAllergy, savedId) => {
      const currentAllergy = currentAllergyMap.get(savedId);

      if (!currentAllergy) {
      } else {
        const nameChanged = savedAllergy.name !== currentAllergy.name;
        const currentParsedDate = parseLocalDateString(currentAllergy.date);
        const dateChanged =
          savedAllergy.date && currentParsedDate && savedAllergy.date !== currentParsedDate;

        if (nameChanged || dateChanged) {
          let savedDisplay = '';
          let currentDisplay = '';

          if (nameChanged && dateChanged) {
            savedDisplay = savedAllergy.date
              ? `${savedAllergy.name} | ${savedAllergy.date}`
              : savedAllergy.name;
            currentDisplay = currentParsedDate
              ? `${currentAllergy.name} | ${currentParsedDate}`
              : currentAllergy.name;
          } else if (nameChanged) {
            savedDisplay = savedAllergy.name || '';
            currentDisplay = currentAllergy.name || '';
          } else if (dateChanged) {
            savedDisplay = savedAllergy.date || '';
            currentDisplay = currentParsedDate || '';
          }

          discrepancies.push({
            field: `Allergy - ${savedAllergy.name} - Date`,
            category: 'allergies',
            savedId: savedId,
            currentId: savedId,
            savedValue: savedDisplay,
            currentValue: currentDisplay,
          });
        }
      }
    });
  }

  private checkMedications(savedItems: any[], currentMedications: any[], discrepancies: any[]) {
    const savedMedicationsItem = savedItems.find((item) => item.name === 'medications');
    const savedMedications = savedMedicationsItem?.value || [];

    const savedMedicationMap = new Map<string, any>();
    const savedMedicationsByName = new Map<string, any>();
    const savedMedicationsWithoutId = [];

    if (!Array.isArray(savedMedications)) {
      return;
    }

    savedMedications.forEach((medication) => {
      const medicationId = medication?.id || medication?.Id;
      const medicationName = medication?.Medication || medication?.MedicationName || '';

      if (medicationId) {
        savedMedicationMap.set(String(medicationId), {
          name: medicationName,
          date: parseLocalDateString(medication?.Date || medication?.StartDate),
          stopDate: parseLocalDateString(medication?.Stop || medication?.StopDate),
          freq: medication?.Freq || medication?.frequency || '',
          dosage: medication?.Dosage || medication?.dosage || '',
        });
      }

      if (medicationName) {
        const normalizedName = medicationName.toLowerCase().trim();
        savedMedicationsByName.set(normalizedName, {
          id: medicationId,
          name: medicationName,
          date: parseLocalDateString(medication?.Date || medication?.StartDate),
          freq: medication?.Freq,
          dosage: medication?.Dosage,
        });

        if (!medicationId) {
          savedMedicationsWithoutId.push({
            name: medicationName,
            date: parseLocalDateString(medication?.Date || medication?.StartDate),
            freq: medication?.Freq,
            dosage: medication?.Dosage,
          });
        }
      }
    });

    const currentMedicationMap = new Map<string, any>();
    const currentMedicationsByName = new Map<string, any>();

    currentMedications?.forEach((medication) => {
      const id = medication.Id;
      const medicationName = medication.MedicationName || '';

      if (id) {
        currentMedicationMap.set(String(id), {
          name: medicationName,
          date: medication.StartDate,
          stopDate: medication.StopDate,
          freq: medication.Freq || '',
          dosage: medication.Dosage || '',
        });
      }

      if (medicationName) {
        const normalizedName = medicationName.toLowerCase().trim();
        currentMedicationsByName.set(normalizedName, {
          id: id,
          name: medicationName,
          date: medication.StartDate,
          freq: medication.Freq,
          dosage: medication.Dosage,
        });
      }
    });

    savedMedicationMap.forEach((savedMedication, savedId) => {
      const currentMedication = currentMedicationMap.get(savedId);

      if (!currentMedication) {
      } else {
        const differences = [];

        if (
          savedMedication.name.toLowerCase().trim() !== currentMedication.name.toLowerCase().trim()
        ) {
          differences.push('Name');
        }

        const savedStartDate = savedMedication.date || '';
        const currentStartDate = currentMedication.date
          ? parseLocalDateString(currentMedication.date)
          : '';
        if (savedStartDate && currentStartDate && savedStartDate !== currentStartDate) {
          differences.push('Start');
        }

        const savedStopDate = savedMedication.stopDate || '';
        const currentStopDate = currentMedication.stopDate
          ? parseLocalDateString(currentMedication.stopDate)
          : '';

        if (
          isValidDateForDiscrepancy(savedStopDate) &&
          isValidDateForDiscrepancy(currentStopDate) &&
          savedStopDate !== currentStopDate
        ) {
          differences.push('Stop');
        }

        const savedFreq = (savedMedication.freq || '').trim();
        const currentFreq = (currentMedication.freq || '').trim();
        if (savedFreq && currentFreq && savedFreq !== currentFreq) {
          differences.push('Freq');
        }

        const savedDosage = (savedMedication.dosage || '').trim();
        const currentDosage = (currentMedication.dosage || '').trim();
        if (savedDosage && currentDosage && savedDosage !== currentDosage) {
          differences.push('Dosage');
        }

        if (differences.length > 0) {
          let savedDisplay = '';
          let currentDisplay = '';

          const savedParts = [];
          const currentParts = [];

          if (differences.includes('Name')) {
            savedParts.push(savedMedication.name);
            currentParts.push(currentMedication.name);
          }

          if (differences.includes('Start') || differences.includes('Stop')) {
            if (differences.includes('Start') && differences.includes('Stop')) {
              const formatDateRange = (start: string, stop: string) => {
                if (isValidDateForDiscrepancy(stop)) {
                  return start ? `${start} to ${stop}` : `to ${stop}`;
                }
                return start || '';
              };
              savedParts.push(formatDateRange(savedStartDate, savedStopDate));
              currentParts.push(formatDateRange(currentStartDate, currentStopDate));
            } else if (differences.includes('Start')) {
              if (isValidDateForDiscrepancy(savedStopDate) && savedStopDate === currentStopDate) {
                if (savedStartDate) savedParts.push(savedStartDate);
                if (currentStartDate) currentParts.push(currentStartDate);
              } else if (
                isValidDateForDiscrepancy(savedStopDate) ||
                isValidDateForDiscrepancy(currentStopDate)
              ) {
                const formatDateRange = (start: string, stop: string) => {
                  if (isValidDateForDiscrepancy(stop)) {
                    return start ? `${start} to ${stop}` : `to ${stop}`;
                  }
                  return start || '';
                };
                const savedRange = formatDateRange(savedStartDate, savedStopDate);
                const currentRange = formatDateRange(currentStartDate, currentStopDate);
                if (savedRange) savedParts.push(savedRange);
                if (currentRange) currentParts.push(currentRange);
              } else {
                if (savedStartDate) savedParts.push(savedStartDate);
                if (currentStartDate) currentParts.push(currentStartDate);
              }
            } else if (differences.includes('Stop')) {
              if (isValidDateForDiscrepancy(savedStopDate))
                savedParts.push(`ends ${savedStopDate}`);
              if (isValidDateForDiscrepancy(currentStopDate))
                currentParts.push(`ends ${currentStopDate}`);
            }
          }

          const freqDosageChanged = differences.includes('Freq') || differences.includes('Dosage');
          if (freqDosageChanged) {
            const savedFreqDosage = [];
            const currentFreqDosage = [];

            if (differences.includes('Freq')) {
              if (savedMedication.freq) savedFreqDosage.push(savedMedication.freq);
              if (currentMedication.freq) currentFreqDosage.push(currentMedication.freq);
            }

            if (differences.includes('Dosage')) {
              if (savedMedication.dosage) savedFreqDosage.push(savedMedication.dosage);
              if (currentMedication.dosage) currentFreqDosage.push(currentMedication.dosage);
            }

            if (savedFreqDosage.length > 0) {
              savedParts.push(savedFreqDosage.join(' | '));
            }

            if (currentFreqDosage.length > 0) {
              currentParts.push(currentFreqDosage.join(' | '));
            }
          }

          savedDisplay = savedParts.join(' | ');
          currentDisplay = currentParts.join(' | ');

          let fieldName = `Medication - ${savedMedication.name}`;
          if (differences.length === 1) {
            fieldName = `Medication - ${savedMedication.name} - ${differences[0]}`;
          } else if (differences.length > 1 && !differences.includes('Name')) {
            fieldName = `Medication - ${savedMedication.name} - ${differences.join(', ')}`;
          }

          discrepancies.push({
            field: fieldName,
            category: 'medications',
            savedId: savedId,
            currentId: savedId,
            savedValue: savedDisplay,
            currentValue: currentDisplay,
          });
        }
      }
    });
  }

  private checkSurgeries(savedItems: any[], currentSurgeries: any[], discrepancies: any[]) {
    const savedSurgeriesItem = savedItems.find((item) => item.name === 'surgeries');
    const savedSurgeries = savedSurgeriesItem?.value || [];

    const savedSurgeryMap = new Map<string, any>();
    const savedSurgeriesWithoutId = [];

    if (!Array.isArray(savedSurgeries)) {
      return;
    }

    savedSurgeries.forEach((surgery) => {
      const surgeryField = surgery?.[1];
      const surgeryId = surgeryField?.id;
      if (surgeryId) {
        savedSurgeryMap.set(String(surgeryId), {
          name: surgeryField?.value,
          date: parseLocalDateString(surgery?.[0]?.value),
        });
      } else if (surgeryField?.value) {
        savedSurgeriesWithoutId.push({
          name: surgeryField?.value,
          date: parseLocalDateString(surgery?.[0]?.value),
        });
      }
    });

    const currentSurgeryMap = new Map<string, any>();
    currentSurgeries?.forEach((surgery) => {
      const id = surgery.Id;
      if (id) {
        currentSurgeryMap.set(String(id), {
          name: surgery.SurgeryName,
          date: surgery.SurgeryDate,
        });
      }
    });

    savedSurgeryMap.forEach((savedSurgery, savedId) => {
      const currentSurgery = currentSurgeryMap.get(savedId);
      if (!currentSurgery) {
      } else {
        const nameChanged = savedSurgery.name !== currentSurgery.name;
        const currentParsedDate = parseLocalDateString(currentSurgery.date);
        const dateChanged =
          savedSurgery.date && currentParsedDate && savedSurgery.date !== currentParsedDate;

        if (nameChanged || dateChanged) {
          let savedDisplay = '';
          let currentDisplay = '';

          if (nameChanged && dateChanged) {
            savedDisplay = savedSurgery.date
              ? `${savedSurgery.name} | ${savedSurgery.date}`
              : savedSurgery.name;
            currentDisplay = currentParsedDate
              ? `${currentSurgery.name} | ${currentParsedDate}`
              : currentSurgery.name;
          } else if (nameChanged) {
            savedDisplay = savedSurgery.name || '';
            currentDisplay = currentSurgery.name || '';
          } else if (dateChanged) {
            savedDisplay = savedSurgery.date || '';
            currentDisplay = currentParsedDate || '';
          }

          discrepancies.push({
            field: `Surgery - ${savedSurgery.name} - Date`,
            category: 'surgeries',
            savedId: savedId,
            currentId: savedId,
            savedValue: savedDisplay,
            currentValue: currentDisplay,
          });
        }
      }
    });
  }
}
